import { useEffect, useRef, useState } from "react";
import * as faceapi from "face-api.js";
import axios from "axios";
import { Download, ImagePlus, Loader2, Sparkles, Ruler, Minus, MoreHorizontal, ArrowLeft, ArrowRight, ChevronDown, SlidersHorizontal, Lock, Eye, Shirt } from "lucide-react";
import { toast } from "sonner";
import { getClientKey, getPhone } from "@/utils/access";
import { authHeaders } from "@/utils/auth";
import { WARDROBE_ENABLED } from "@/utils/flags";
import { pluralTryons } from "./accountUi";
import { confirmPhoneViaTelegram } from "@/utils/telegram";
import { FaceTexture } from "@/components/FaceTexture";

import { API, BACKEND_URL } from "../utils/api";
import {
  loadSession,
  saveSession,
  flushSession,
  addPendingJob,
  removePendingJob,
  fetchImageDataUrl,
} from "../utils/session";

const MASK_EYE_Y = 215 / 500;
const MASK_EYE_DX = 158 / 500;
const DETECT_TIMEOUT_MS = 90000;
const OVERLAY_SCALE = 1.35;
const BROW_BASE_OFF = 0.35; // доля высоты глаза ниже линии лендмарок брови = основание брови

const MASKS = [
  { id: "marquardt", name: "Маска", src: "/masks/marquardt.png", kind: "face", ax: 0.5, ay: MASK_EYE_Y, aspect: 1 },
  { id: "profile", name: "Профиль", src: "/masks/profile.png", kind: "overlay", ax: 0.5, ay: 0.5, aspect: 1 },
  { id: "lines1", name: "1 линия", src: "/masks/lines1.png", dash: "/masks/lines1_dash.png", kind: "overlay", ax: 0.5, ay: 0.5, aspect: 1, bind: { pts: ["brow"], ys: [0.3593] } },
  { id: "lines3", name: "3 линии", src: "/masks/lines3.png", dash: "/masks/lines3_dash.png", kind: "overlay", ax: 0.5, ay: 0.5, aspect: 1, bind: { pts: ["hair", "brow", "nose"], ys: [0.0877, 0.3593, 0.624] } },
  { id: "lines4", name: "4 линии", src: "/masks/lines4.png", dash: "/masks/lines4_dash.png", kind: "overlay", ax: 0.5, ay: 0.5, aspect: 1, bind: { pts: ["hair", "brow", "nose", "chin"], ys: [0.0195, 0.3593, 0.6657, 0.9889] } },
  { id: "lines5", name: "5 линий", src: "/masks/lines5.png", dash: "/masks/lines5_dash.png", kind: "overlay", ax: 0.5, ay: 0.5, aspect: 861 / 718, bind: { pts: ["hair", "brow", "nose", "lips", "chin"], ys: [0.0871, 0.3043, 0.5226, 0.7398, 0.957] } },
  { id: "diagonal", name: "Диагональ", src: "/masks/diagonal.png", dash: "/masks/diagonal_dash.png", kind: "overlay", ax: 0.5, ay: 0.5, aspect: 1 },
  { id: "cross", name: "Крест", src: "/masks/cross.png", dash: "/masks/cross_dash.png", kind: "overlay", ax: 0.5, ay: 0.5, aspect: 1 },
  { id: "crosshair", name: "Прицел", src: "/masks/crosshair.png", dash: "/masks/crosshair_dash.png", kind: "overlay", ax: 0.5, ay: 0.5, aspect: 1, bind: { pts: ["eye", "nose"], ys: [0.4234, 0.7131], cxMode: "box" } },
];

let useTinyLandmarks = false;
let modelsPromise = null;
const loadModels = () => {
  if (!modelsPromise) {
    modelsPromise = (async () => {
      await faceapi.nets.tinyFaceDetector.loadFromUri("/models");
      try {
        await faceapi.nets.faceLandmark68Net.loadFromUri("/models");
        useTinyLandmarks = false;
      } catch {
        await faceapi.nets.faceLandmark68TinyNet.loadFromUri("/models");
        useTinyLandmarks = true;
      }
    })();
  }
  return modelsPromise;
};

const clampW = (w) => Math.min(Math.max(w, 60), 3000);

// Балл-загадка: ~1с крутятся случайные цифры, затем 0.7с добор до настоящего балла.
// При восстановленной сессии (instant) показываем сразу — разбор не «проигрывается» заново
const MysteryScore = ({ value, instant = false }) => {
  const [display, setDisplay] = useState(instant ? value : 0);
  useEffect(() => {
    if (instant) {
      setDisplay(value);
      return;
    }
    const scramble = setInterval(() => setDisplay(Math.floor(55 + Math.random() * 44)), 70);
    const t = setTimeout(() => {
      clearInterval(scramble);
      const start = performance.now();
      const tick = (now) => {
        const p = Math.min((now - start) / 700, 1);
        setDisplay(Math.round((1 - Math.pow(1 - p, 3)) * value));
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }, 1000);
    return () => {
      clearInterval(scramble);
      clearTimeout(t);
    };
  }, [value, instant]);
  return <>{display}</>;
};

const DETECT_ATTEMPTS = [
  { inputSize: 608, scoreThreshold: 0.3 },
  { inputSize: 1280, scoreThreshold: 0.2 },
  { inputSize: 992, scoreThreshold: 0.12 },
  { inputSize: 1600, scoreThreshold: 0.08 },
];

const detectFace = async (img) => {
  let lastErr = null;
  for (const opts of DETECT_ATTEMPTS) {
    try {
      const det = await faceapi
        .detectSingleFace(img, new faceapi.TinyFaceDetectorOptions(opts))
        .withFaceLandmarks(useTinyLandmarks);
      if (det) return det;
    } catch (err) {
      lastErr = err;
    }
  }
  if (lastErr) throw lastErr;
  return null;
};

const MASK_COLORS = [
  { id: "gold", name: "Золотой", hex: "#C2A878" },
  { id: "green", name: "Зелёный", hex: "#34D399" },
  { id: "red", name: "Красный", hex: "#EF4444" },
  { id: "black", name: "Чёрный", hex: "#141414" },
  { id: "white", name: "Белый", hex: "#F4F4F0" },
];

const LINE_DEFS = [
  { key: "hairY", label: "Линия роста волос" },
  { key: "browY", label: "Брови" },
  { key: "eyeY", label: "Глаза" },
  { key: "noseY", label: "Основание носа" },
  { key: "lipsY", label: "Губы" },
  { key: "chinY", label: "Подбородок" },
];

const tintMask = (color, src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.src = src;
    img.onerror = () => resolve(null);
    img.onload = () => {
      const draw = () => {
        const c = document.createElement("canvas");
        c.width = img.naturalWidth;
        c.height = img.naturalHeight;
        const ctx = c.getContext("2d");
        ctx.drawImage(img, 0, 0);
        ctx.globalCompositeOperation = "source-in";
        ctx.fillStyle = color;
        ctx.fillRect(0, 0, c.width, c.height);
        resolve(c);
      };
      // iOS Safari: drawImage по недекодированному PNG рисует картинку ЧАСТИЧНО
      // (на канвасе «2 линии вместо 4») — ждём полного декодирования
      if (img.decode) img.decode().then(draw).catch(draw);
      else draw();
    };
  });

const SCAN_STEPS = [
  "Сканируем лицо",
  "Измеряем пропорции",
  "Сверяем с золотым сечением",
  "Подбираем формы стрижек",
];

const DETECT_STEPS = {
  models: ["Загружаем детектор лица", "Готовим измерительные точки"],
  detect: ["Ищем лицо на фото", "Строим карту пропорций", "Подгоняем маску"],
};

const REC_FILTERS = [
  ["all", "Все"],
  ["short", "Короткие"],
  ["medium", "Средние"],
  ["long", "Длинные"],
];
const LENGTH_RU = { short: "Короткая", medium: "Средняя", long: "Длинная" };

// Премиальный экран ожидания: фото, скан-линия, пульс-кольцо, процент, статусы.
// fill=true — на весь экран поверх редактора (загрузка детектора), иначе — рамка на экране разбора
const AnalysisScan = ({ photoUrl, steps = SCAN_STEPS, note, fill = false, testid = "analysis-loading" }) => {
  const [pct, setPct] = useState(0);
  const [step, setStep] = useState(0);
  useEffect(() => {
    const t1 = setInterval(
      () => setPct((p) => Math.min(99, p + Math.max(1, Math.round((99 - p) * 0.035)))),
      280
    );
    const t2 = setInterval(() => setStep((s) => (s + 1) % steps.length), 2600);
    return () => {
      clearInterval(t1);
      clearInterval(t2);
    };
  }, [steps.length]);

  const visual = (
    <>
      {photoUrl && (
        <img src={photoUrl} alt="Фото на анализе" className="h-full w-full object-cover opacity-75" />
      )}
      <div className="absolute inset-0 bg-[#0A0A0A]/40" />
      {/* уголки рамки сканера */}
      <span className="absolute left-3 top-3 h-5 w-5 border-l border-t border-[#C2A878]/80" />
      <span className="absolute right-3 top-3 h-5 w-5 border-r border-t border-[#C2A878]/80" />
      <span className="absolute bottom-3 left-3 h-5 w-5 border-b border-l border-[#C2A878]/80" />
      <span className="absolute bottom-3 right-3 h-5 w-5 border-b border-r border-[#C2A878]/80" />
      {/* пульс-кольцо на лице */}
      <div
        aria-hidden="true"
        className="scan-pulse absolute left-1/2 top-[38%] h-40 w-40 rounded-full border border-[#C2A878]/50 shadow-[0_0_24px_rgba(194,168,120,0.25)]"
      />
      {/* скан-линия */}
      <div
        aria-hidden="true"
        className="scan-line absolute inset-x-0 -top-12 h-24"
      >
        <div className="h-full w-full bg-gradient-to-b from-transparent via-[#C2A878]/20 to-transparent" />
        <div className="absolute inset-x-6 top-1/2 h-px bg-[#C2A878] shadow-[0_0_16px_rgba(194,168,120,0.9)]" />
      </div>
      {/* процент */}
      <div className="absolute inset-x-0 bottom-4 text-center">
        <span className="font-cormorant text-4xl font-light text-[#F4F4F0] drop-shadow-[0_2px_10px_rgba(0,0,0,0.8)]" data-testid="scan-progress">
          {pct}
          <span className="text-xl italic text-[#C2A878]">%</span>
        </span>
      </div>
    </>
  );

  const caption = (
    <div className={fill ? "absolute inset-x-0 bottom-16 text-center" : "mt-6 text-center"}>
      <p
        key={step}
        className="rise-in font-jetbrains text-[11px] uppercase tracking-[0.3em] text-[#C2A878]"
        data-testid="scan-status"
      >
        {steps[step]}
      </p>
      {note && (
        <p className="mt-2 font-jetbrains text-[10px] uppercase tracking-[0.2em] text-[#525252]">
          {note}
        </p>
      )}
    </div>
  );

  if (fill) {
    return (
      <div className="absolute inset-0 z-40 bg-[#0A0A0A]/55 backdrop-blur-[2px]" data-testid={testid} aria-label="Подготовка в процессе">
        {visual}
        {caption}
      </div>
    );
  }
  return (
    <div className="px-6 pt-8" data-testid={testid} aria-label="Анализ в процессе">
      <div className="relative mx-auto aspect-[3/4] max-h-[54dvh] w-full overflow-hidden border border-white/10">
        {visual}
      </div>
      {caption}
    </div>
  );
};

// Тонкие линейные иконки особенностей лица (в стиле макета карточки формы лица)
const JawIcon = ({ size = 20, strokeWidth = 1.5, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" className={className} aria-hidden="true">
    <path d="M6 4v6a6 6 0 0 0 12 0V4" />
  </svg>
);

const ProfileIcon = ({ size = 20, strokeWidth = 1.5, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" className={className} aria-hidden="true">
    <path d="M10 3c3 2 4.5 4.5 3 7-1 1.7-1 2.8.2 4-1.8.8-2.4 2-1.6 3.5.6 1.2.6 2.5-.6 3.5" />
  </svg>
);

// Иконка чипа по ключевым словам особенности
const featureIcon = (text) => {
  const t = String(text || "").toLowerCase();
  if (t.includes("глаз") || t.includes("взгляд") || t.includes("бров")) return Eye;
  if (t.includes("челюст") || t.includes("подбород")) return JawIcon;
  if (t.includes("скул") || t.includes("щёк") || t.includes("щек") || t.includes("профил") || t.includes("овал") || t.includes("контур") || t.includes("линия")) return ProfileIcon;
  return Sparkles;
};

// Запасные описания формы — для разборов, сделанных до появления face_shape_desc
const SHAPE_DESCS = {
  овальная: "Сбалансированные пропорции и мягкие линии.",
  круглая: "Мягкие линии и равные ширина и длина.",
  квадратная: "Сильная челюсть и графичные контуры.",
  треугольная: "Выразительная нижняя треть и узкий лоб.",
  продолговатая: "Вытянутый силуэт и вертикальные линии.",
  сердцевидная: "Широкий лоб и изящный подбородок.",
};

// Карточка «Форма лица»: название, описание, балл в золотом кольце, чипы особенностей
const FaceShapeCard = ({ shape, desc, score, features = [], instant = false }) => {
  const fallback = SHAPE_DESCS[String(shape || "").trim().toLowerCase().split(/\s+/)[0]] || "";
  const text = desc || fallback;
  return (
    <div
      className="rise-in relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-b from-[#C2A878]/[0.06] to-transparent p-5"
      data-testid="face-shape-card"
    >
      {/* Тёплое свечение в углу — как на карточках кабинета */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-10 -top-10 h-36 w-36 rounded-full opacity-25"
        style={{ background: "radial-gradient(circle, #C2A878 0%, transparent 70%)" }}
      />
      <div className="relative flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="font-jetbrains text-[9px] uppercase tracking-[0.3em] text-[#A3A3A3]">
            Форма лица
          </p>
          <h2
            className="mt-2.5 font-cormorant text-4xl font-light leading-none tracking-tight text-[#F4F4F0]"
            data-testid="ai-face-shape"
          >
            {shape}
          </h2>
          {text && (
            <p className="mt-3 text-sm leading-relaxed text-[#A3A3A3]" data-testid="face-shape-desc">
              {text}
            </p>
          )}
        </div>
        <div className="shrink-0 text-center">
          <div
            className="flex h-20 w-20 items-baseline justify-center rounded-full border border-[#C2A878] pt-6 shadow-[0_0_28px_rgba(194,168,120,0.22)]"
            data-testid="face-shape-score-ring"
            aria-label={`Совпадение с золотым сечением: ${score} из 100`}
          >
            <span className="font-cormorant text-2xl font-light leading-none text-[#F4F4F0]" data-testid="ai-score">
              <MysteryScore value={score} instant={instant} />
            </span>
            <span className="ml-0.5 font-jetbrains text-[10px] text-[#A3A3A3]">/100</span>
          </div>
          <p className="mt-2 font-jetbrains text-[7px] uppercase tracking-[0.25em] text-[#A3A3A3]">
            совпадение
          </p>
        </div>
      </div>
      {features.length > 0 && (
        <div className="relative mt-5 grid grid-cols-3 gap-2" data-testid="ai-features">
          {features.slice(0, 3).map((f, i) => {
            const Icon = featureIcon(f);
            return (
              <div
                key={i}
                className="flex min-w-0 flex-col items-start gap-2 rounded-2xl border border-white/10 bg-white/[0.02] px-3 py-3"
                data-testid={`ai-feature-${i}`}
              >
                <Icon size={18} strokeWidth={1.5} className="shrink-0 text-[#C2A878]" />
                <span className="min-w-0 break-words text-xs leading-snug text-[#F4F4F0]">{f}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Ошибки примерки: 403 — номер не подтверждён (предлагаем подтвердить прямо из тоста),
// 429 — лимит исчерпан (ведём на тарифы), 52x/Cloudflare — перегруз или перезапуск сервера
const CF_STATUS = new Set([520, 521, 522, 523, 524, 525, 526, 527, 530]);
const CLOUDFLARE_MSG =
  "Сервер генерации перегружен или перезапускается. Подождите 30–60 секунд и попробуйте снова — генерация не списана. Если она успела завершиться, результат уже в истории кабинета.";

const notifyTryOnError = (err) => {
  const status = err.response?.status;
  const raw = err.response?.data;
  if (CF_STATUS.has(status) || (typeof raw === "string" && /cloudflare|origin web server/i.test(raw))) {
    toast.error(CLOUDFLARE_MSG);
    return;
  }
  if (!status) {
    toast.error("Нет связи с сервером. Проверьте интернет и попробуйте ещё раз — генерация не списана.");
    return;
  }
  const detail =
    typeof err.response?.data?.detail === "string"
      ? err.response.data.detail
      : "Примерка не удалась. Попробуйте ещё раз.";
  if (status === 403) {
    toast.error(detail, {
      action: {
        label: "Подтвердить",
        onClick: async () => {
          try {
            await confirmPhoneViaTelegram();
            toast.success("Номер подтверждён — бесплатные примерки открыты");
          } catch (e) {
            if (e?.message !== "cancelled") {
              toast.error("Не удалось подтвердить номер. Откройте форму доступа на главном экране.");
            }
          }
        },
      },
    });
    return;
  }
  toast.error(
    detail,
    status === 429
      ? { action: { label: "Тарифы", onClick: () => window.location.assign("/account") } }
      : undefined
  );
};

// Ужатие фото для ИИ-разбора: 1024px достаточно для vision-анализа, а загрузка
// становится в 3-4 раза легче — на задушенных троттлингом соединениях большой запрос
// часто не долетает до сервера (подтверждено продакшн-логами)
const downscaleForAnalysis = (dataUrl) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, 1024 / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL("image/jpeg", 0.8));
    };
    img.onerror = () => resolve(dataUrl); // не смогли ужать — отправляем как есть
    img.src = dataUrl;
  });

// Опрос фоновой задачи генерации. Обрывы Cloudflare (520) при опросе не страшны —
// короткие запросы просто повторяются; генерация идёт на сервере независимо.
const pollJob = async (jobId, headers) => {
  const started = Date.now();
  let fails = 0;
  while (Date.now() - started < 420000) {
    await new Promise((r) => setTimeout(r, 3000));
    try {
      const { data } = await axios.get(`${API}/jobs/${jobId}`, { timeout: 20000, headers });
      fails = 0;
      if (data.status === "done") return data;
      if (data.status === "error") {
        throw { response: { status: 502, data: { detail: data.error } } };
      }
    } catch (err) {
      if (typeof err?.response?.data?.detail === "string") throw err;
      fails += 1;
      if (fails >= 10) throw err;
    }
  }
  throw {
    response: {
      status: 504,
      data: { detail: "Генерация заняла слишком много времени. Если она завершилась, результат уже в истории кабинета." },
    },
  };
};

export const MaskEditor = ({ photoUrl, onNewPhoto, view, setView, topInset = 0, restore = null }) => {
  const restoreRef = useRef(restore); // сессия применяется один раз после детекции лица
  const metricsRef = useRef(null); // замеры пропорций с 68 точек — уходят в ИИ при разборе
  const skipAnalysisToastRef = useRef(false); // не дублируем тост «Разбор готов» при восстановлении
  const [restoredUi, setRestoredUi] = useState(false); // контент из сессии — без повторных анимаций
  const willRestore = Boolean(restore); // при восстановлении не показываем сцену детекции заново
  const stageRef = useRef(null);
  const imgRef = useRef(null);
  const maskHandleRef = useRef(null);
  const resultRef = useRef(null);
  const pointersRef = useRef(new Map());
  const dragRef = useRef(null);
  const poseRef = useRef({ cx: 0, cy: 0, w: 200, rot: 0 });
  const mapRef = useRef({ scale: 1, offX: 0, offY: 0 });
  const linesRef = useRef(null);
  const fitRef = useRef(null);
  const [pose, setPoseState] = useState(poseRef.current);
  const [status, setStatus] = useState("models");
  const [faceFound, setFaceFound] = useState(true);
  const [maskId, setMaskId] = useState("marquardt");
  const [dashed, setDashed] = useState(false);
  const [color, setColor] = useState("#141414");
  const [intensity, setIntensity] = useState(1);
  const [tintedSrc, setTintedSrc] = useState(null);
  const [showLines, setShowLines] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [analysisError, setAnalysisError] = useState("");
  const [savePreview, setSavePreview] = useState(null);
  const [detectError, setDetectError] = useState("");
  const [tryOnLoading, setTryOnLoading] = useState(-1);
  const [tryOnImage, setTryOnImage] = useState(null);
  const [triedMap, setTriedMap] = useState({}); // примеренные варианты на этом фото: { [название]: image } — повторный просмотр бесплатный
  const [analysisTab, setAnalysisTab] = useState("analysis");
  const [recFilter, setRecFilter] = useState("all");
  const [openZone, setOpenZone] = useState(null);
  const [tryOnRec, setTryOnRec] = useState("");
  const [tryOnRemaining, setTryOnRemaining] = useState(null);
  const [refImage, setRefImage] = useState(null);
  const [refLoading, setRefLoading] = useState(false);
  const refInputRef = useRef(null);
  const [isMaster, setIsMaster] = useState(false);
  const [canSave, setCanSave] = useState(false);
  const [techCard, setTechCard] = useState(null);
  const [techLoading, setTechLoading] = useState(false);
  const [techCardIdx, setTechCardIdx] = useState(null);
  const [showBefore, setShowBefore] = useState(false);
  const [showStyle, setShowStyle] = useState(false);
  const [haircare, setHaircare] = useState(null);
  const [careLoading, setCareLoading] = useState(false);
  // Полный лук: образ (одежда) поверх фото с примеренной стрижкой
  const [lookOpen, setLookOpen] = useState(false);
  const [lookLoading, setLookLoading] = useState(false);
  const [lookPreview, setLookPreview] = useState(null); // {mode, title, why, clothes_en, item_ids}
  const [lookItems, setLookItems] = useState(null);     // кэш вещей гардероба
  const [lookGenerating, setLookGenerating] = useState(false);
  const [lookImage, setLookImage] = useState(null);
  const [lookTitle, setLookTitle] = useState("");
  // Подтверждение списания примерок перед генерацией: {cost, run}
  const [spendAsk, setSpendAsk] = useState(null);
  const askSpend = (cost, run) => setSpendAsk({ cost, run });

  const mask = MASKS.find((m) => m.id === maskId) || MASKS[0];
  const activeSrc = dashed && mask.dash ? mask.dash : mask.src;

  const setPose = (updater) => {
    setPoseState((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      poseRef.current = next;
      return next;
    });
  };

  // Косметические настройки маски — тоже часть сессии
  useEffect(() => {
    saveSession({ maskId, color, intensity, dashed });
  }, [maskId, color, intensity, dashed]);

  const fitAnimTimer = useRef(null);
  const [fitAnim, setFitAnim] = useState(false);

  const fitMask = (m) => {
    const f = fitRef.current;
    if (!f) return;
    setFitAnim(true);
    clearTimeout(fitAnimTimer.current);
    fitAnimTimer.current = setTimeout(() => setFitAnim(false), 500);
    if (m.kind === "face") {
      setPose({ cx: f.eyeCx, cy: f.eyeCy, w: f.eyeDist / MASK_EYE_DX, rot: f.rot });
      return;
    }
    const isProfile = f.eyeDist < f.boxW * 0.22;
    const A = f.anchors;
    if (m.bind && A && !isProfile) {
      const mx = m.bind.vx ?? 0.5;
      const top = A[m.bind.pts[0]];
      const bot = m.bind.pts.length > 1 ? A[m.bind.pts[m.bind.pts.length - 1]] : null;
      const ya = m.bind.ys[0] * m.aspect;
      const yb = m.bind.ys[m.bind.ys.length - 1] * m.aspect;
      if (bot) {
        // similarity fit: mask anchor segment (top line -> bottom line) onto face axis segment
        const vx = bot.x - top.x;
        const vy = bot.y - top.y;
        const faceLen = Math.hypot(vx, vy);
        const maskLen = Math.abs(yb - ya);
        const w = faceLen / maskLen;
        if (faceLen > 20 && w > 40 && w < 3000) {
          const rot = Math.atan2(vy, vx) - Math.PI / 2;
          const cos = Math.cos(rot);
          const sin = Math.sin(rot);
          const tx = top.x - (mx * w * cos - ya * w * sin);
          const ty = top.y - (mx * w * sin + ya * w * cos);
          const pose = {
            cx: tx + (0.5 * w * cos - (m.aspect / 2) * w * sin),
            cy: ty + (0.5 * w * sin + (m.aspect / 2) * w * cos),
            w,
            rot,
          };
          if (m.bind.cxMode === "box") pose.cx = f.boxCx;
          setPose(pose);
          return;
        }
      } else if (top) {
        // single line: keep default scale, place the line on its landmark
        const w = (f.boxBot - f.boxTop) * OVERLAY_SCALE;
        setPose({
          cx: top.x + (0.5 - mx) * w,
          cy: top.y + (0.5 - ya) * w * m.aspect,
          w,
          rot: f.rot,
        });
        return;
      }
    }
    const h = f.boxBot - f.boxTop;
    setPose({
      cx: f.boxCx,
      cy: (f.boxTop + f.boxBot) / 2,
      w: h * OVERLAY_SCALE,
      rot: isProfile ? 0 : f.rot,
    });
  };

  const selectMask = (id) => {
    const m = MASKS.find((x) => x.id === id);
    if (!m) return;
    const willDash = m.dash ? dashed : false;
    const cached = tintCacheRef.current.get(`${willDash && m.dash ? m.dash : m.src}|${color}`);
    if (cached) setTintedSrc(cached); // мгновенная подмена в том же кадре, без промежуточной маски
    setMaskId(id);
    if (!m.dash) setDashed(false);
    if (fitRef.current) fitMask(m);
  };

  const tintCacheRef = useRef(new Map());
  const lastActiveSrc = useRef(null);

  useEffect(() => {
    const key = `${activeSrc}|${color}`;
    const cached = tintCacheRef.current.get(key);
    if (cached) {
      setTintedSrc(cached);
      lastActiveSrc.current = activeSrc;
      return;
    }
    let cancelled = false;
    // чужую маску не показываем; при смене только цвета старый оттенок держим до готовности нового
    if (lastActiveSrc.current !== activeSrc) setTintedSrc(null);
    lastActiveSrc.current = activeSrc;
    tintMask(color, activeSrc).then((canvas) => {
      if (!canvas || cancelled) return;
      const dataUrl = canvas.toDataURL("image/png");
      const cache = tintCacheRef.current;
      if (cache.size > 30) cache.delete(cache.keys().next().value);
      cache.set(key, dataUrl);
      setTintedSrc(dataUrl);
    });
    return () => { cancelled = true; };
  }, [color, activeSrc]);

  // фоновая окраска всех масок — переключения без мигания
  useEffect(() => {
    if (status === "idle") return;
    let cancelled = false;
    const queue = [];
    MASKS.forEach((m) => {
      queue.push(m.src);
      if (m.dash) queue.push(m.dash);
    });
    const step = (i) => {
      if (cancelled || i >= queue.length) return;
      const src = queue[i];
      const key = `${src}|${color}`;
      if (tintCacheRef.current.has(key)) {
        step(i + 1);
        return;
      }
      tintMask(color, src).then((canvas) => {
        if (!cancelled && canvas) {
          const cache = tintCacheRef.current;
          if (cache.size > 40) cache.delete(cache.keys().next().value);
          cache.set(key, canvas.toDataURL("image/png"));
        }
        setTimeout(() => step(i + 1), 80);
      });
    };
    const t = setTimeout(() => step(0), 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [status, color]);

  // Тост «Разбор готов» — только при свежем разборе; при восстановлении из сессии молчим
  useEffect(() => {
    if (!analysis) return;
    if (skipAnalysisToastRef.current) {
      skipAnalysisToastRef.current = false;
      return;
    }
    toast.success("Разбор готов");
  }, [analysis]);

  const tryOnRef = useRef(null);

  useEffect(() => {
    if (!tryOnImage || !tryOnRef.current) return;
    const t = setTimeout(() => {
      tryOnRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 200);
    return () => clearTimeout(t);
  }, [tryOnImage]);

  // Новое фото — кэш примерок и лука сбрасывается: на другом фото вариант можно примерить снова
  useEffect(() => {
    setTriedMap({});
    setLookImage(null);
    setLookTitle("");
    setLookPreview(null);
    setLookOpen(false);
  }, [photoUrl]);


  const measureMap = () => {
    const img = imgRef.current;
    const scale = Math.min(img.clientWidth / img.naturalWidth, img.clientHeight / img.naturalHeight);
    const rendW = img.naturalWidth * scale;
    const rendH = img.naturalHeight * scale;
    mapRef.current = {
      scale,
      offX: (img.clientWidth - rendW) / 2,
      offY: (img.clientHeight - rendH) / 2,
      rendW,
      rendH,
    };
    return mapRef.current;
  };

  // Восстановление: карточки примерок лежат на сервере, тянем по id обратно в data URL
  const applyRestoredImages = async (r) => {
    const ids = r.triedIds || {};
    const names = Object.keys(ids);
    if (!names.length) return;
    const map = {};
    await Promise.all(
      names.map(async (n) => {
        try {
          map[n] = await fetchImageDataUrl(ids[n]);
        } catch {}
      })
    );
    if (!Object.keys(map).length) return;
    setTriedMap(map);
    if (r.tryOnRec && map[r.tryOnRec]) {
      setTryOnImage(map[r.tryOnRec]);
      setTryOnRec(r.tryOnRec);
    }
  };

  // Незавершённые генерации переживают закрытие приложения: просто продолжаем опрос
  const resumePendingJobs = (r) => {
    const jobs = r.pendingJobs || [];
    if (!jobs.length) return;
    const headers = authHeaders();
    jobs.forEach((j) => {
      removePendingJob(j.id);
      // Разбор уже восстановлен из сессии — задача-дубль (сирота после сетевого ретрая),
      // не крутим сканер повторно
      if (j.kind === "analyze" && r.analysis) return;
      if (j.kind === "analyze") setAnalyzing(true);
      if (j.kind === "haircare") setCareLoading(true);
      if (j.kind === "look") setLookGenerating(true);
      pollJob(j.id, headers)
        .then((data) => {
          if (j.kind === "analyze" && data?.face_shape) {
            setAnalysis(data);
            saveSession({ analysis: data });
            toast.success("Разбор готов");
          } else if (j.kind === "tryon" && data?.image) {
            setTriedMap((m) => ({ ...m, [j.recName]: data.image }));
            const cur = loadSession() || {};
            saveSession({ triedIds: { ...(cur.triedIds || {}), [j.recName]: data.image_id } });
            toast.success(`Примерка «${j.recName}» готова`);
          } else if (j.kind === "haircare" && data?.image_id) {
            setHaircare(data);
            saveSession({ haircare: data });
            toast.success("Подбор ухода готов");
          } else if (j.kind === "look" && data?.image) {
            setLookImage(data.image);
            setLookTitle(j.lookTitle || "");
            setLookOpen(false);
            saveSession({ lookImageId: data.image_id, lookTitle: j.lookTitle || "" });
            toast.success("Образ готов");
          }
        })
        .catch(() => {})
        .finally(() => {
          flushSession();
          if (j.kind === "analyze") setAnalyzing(false);
          if (j.kind === "haircare") setCareLoading(false);
          if (j.kind === "look") setLookGenerating(false);
        });
    });
  };

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setStatus("models");
      setFaceFound(true);
      setAnalysis(null);
      setTryOnLoading(-1);
      setTryOnImage(null);
      setTryOnRec("");
      setShowBefore(false);
      setShowLines(false);
      setDetectError("");
      linesRef.current = null;
      fitRef.current = null;
      metricsRef.current = null;
      try {
        const img = imgRef.current;
        await img.decode();
        await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
        await loadModels();
        if (cancelled) return;
        setStatus("detecting");
        const det = await Promise.race([
          detectFace(img),
          new Promise((_, reject) => setTimeout(() => reject(new Error("detect timeout")), DETECT_TIMEOUT_MS)),
        ]);
        if (cancelled) return;
        const { scale, offX, offY, rendW, rendH } = measureMap();
        if (det) {
          const pts = det.landmarks.positions;
          const avg = (from, to) => {
            let x = 0, y = 0;
            for (let i = from; i <= to; i++) { x += pts[i].x; y += pts[i].y; }
            return { x: x / (to - from + 1), y: y / (to - from + 1) };
          };
          const eyeL = avg(36, 41);
          const eyeR = avg(42, 47);
          const eyeDist = Math.hypot(eyeR.x - eyeL.x, eyeR.y - eyeL.y) * scale;
          const rot = Math.atan2(eyeR.y - eyeL.y, eyeR.x - eyeL.x);
          const cx = offX + ((eyeL.x + eyeR.x) / 2) * scale;
          const cy = offY + ((eyeL.y + eyeR.y) / 2) * scale;
          const eyeH =
            ((pts[40].y + pts[41].y) / 2 - (pts[37].y + pts[38].y) / 2 +
              (pts[46].y + pts[47].y) / 2 - (pts[43].y + pts[44].y) / 2) / 2 * scale;
          const browMid = avg(17, 26);
          const brow = { x: offX + browMid.x * scale, y: offY + browMid.y * scale + BROW_BASE_OFF * eyeH };
          const nose = { x: offX + pts[33].x * scale, y: offY + pts[33].y * scale };
          const lips = {
            x: offX + ((pts[51].x + pts[57].x) / 2) * scale,
            y: offY + ((pts[51].y + pts[57].y) / 2) * scale,
          };
          const chin = { x: offX + pts[8].x * scale, y: offY + pts[8].y * scale };
          const hair = { x: 2 * brow.x - nose.x, y: brow.y - (nose.y - brow.y) };
          // Замеры пропорций в «сырых» координатах точек (от масштаба не зависят):
          // длина/ширина лица, челюсть/скулы, вертикальные трети. Лоб оцениваем от линии бровей.
          const pDist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
          const faceW = pDist(pts[0], pts[16]);
          const jawW = pDist(pts[4], pts[12]);
          // Дополнительные пропорции из тех же точек (база FACE_METRICS.md):
          // «пятые части» (глаза/нос/рот), ширина скул, правило 5.5 см (ухо→подбородок)
          const eyeW = (pDist(pts[36], pts[39]) + pDist(pts[42], pts[45])) / 2;
          const intercanthal = pDist(pts[39], pts[42]);
          const noseW = pDist(pts[31], pts[35]);
          const mouthW = pDist(pts[48], pts[54]);
          const cheekW = Math.max(pDist(pts[1], pts[15]), pDist(pts[2], pts[14]), pDist(pts[3], pts[13]));
          const earChin = pDist(pts[1], pts[8]);
          const eyeRawY = (eyeL.y + eyeR.y) / 2;
          const noseBaseY = pts[30].y;
          // Лоб оцениваем от расстояния бровь–глаз (калибровка ×2.6 ≈ треть лица у среднего лица),
          // с полом в 0.75 средней трети — иначе все лица читаются шире/короче реального
          const foreheadH = Math.max((browMid.y - eyeRawY) * 2.6, (noseBaseY - browMid.y) * 0.75);
          const midThird = noseBaseY - browMid.y;
          const lowThird = pts[8].y - noseBaseY;
          const thirdSum = foreheadH + midThird + lowThird;
          const eyeNoseL = pDist(pts[36], pts[30]);
          const eyeNoseR = pDist(pts[45], pts[30]);
          const yawPct = Math.round((Math.abs(eyeNoseL - eyeNoseR) / Math.max(eyeNoseL, eyeNoseR)) * 100);
          metricsRef.current =
            faceW > 0 && thirdSum > 0 && eyeW > 0 && intercanthal > 0 && noseW > 0 && cheekW > 0
              ? {
                  len_width: +((foreheadH + midThird + lowThird) / faceW).toFixed(2),
                  jaw_ratio: +(jawW / faceW).toFixed(2),
                  thirds: [foreheadH, midThird, lowThird].map((v) => Math.round((v / thirdSum) * 100)),
                  tilt_deg: Math.round(Math.abs(rot) * 180 / Math.PI), // наклон головы — замеры под углом менее точны
                  yaw_pct: yawPct, // поворот вполоборота: асимметрия глаза-носа; при высокой цифре замерам не доверяем
                  eye_spacing: +(intercanthal / eyeW).toFixed(2), // пятые части: канон ≈ 1.0
                  nose_ratio: +(noseW / intercanthal).toFixed(2), // канон ≈ 1.0
                  mouth_ratio: +(mouthW / noseW).toFixed(2), // канон ≈ 1.5
                  jaw_cheek: +(jawW / cheekW).toFixed(2), // R2: челюсть к скулам
                  cheek_ratio: +(cheekW / faceW).toFixed(2), // скулы к верхней ширине лица
                  ear_chin: +(earChin / cheekW).toFixed(2), // прокси правила 5.5 см
                }
              : null;
          linesRef.current = { hairY: hair.y, browY: brow.y, eyeY: cy, noseY: nose.y, lipsY: lips.y, chinY: chin.y, centerX: cx };
          let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
          for (const p of pts) {
            if (p.x < minX) minX = p.x;
            if (p.x > maxX) maxX = p.x;
            if (p.y < minY) minY = p.y;
            if (p.y > maxY) maxY = p.y;
          }
          const boxW = (maxX - minX) * scale;
          const boxH = (maxY - minY) * scale;
          fitRef.current = {
            eyeCx: cx,
            eyeCy: cy,
            eyeDist,
            rot,
            boxW,
            boxCx: offX + ((minX + maxX) / 2) * scale,
            boxTop: offY + minY * scale - 0.5 * boxH,
            boxBot: offY + maxY * scale,
            anchors: { hair, brow, eye: { x: cx, y: cy }, nose, lips, chin },
          };
          fitMask(mask);
          // Сессия: молча возвращаем человека на то же место после случайного закрытия
          const r = restoreRef.current;
          restoreRef.current = null;
          // Восстанавливаем только если сессия от ЭТОГО фото — иначе на новое лицо
          // натянутся чужая поза маски и чужой разбор
          if (r && r.photo === photoUrl) {
            setRestoredUi(true);
            skipAnalysisToastRef.current = Boolean(r.analysis);
            // Позу маски из кеша НЕ восстанавливаем (решение владельца): подгоняем заново автоматически
            if (r.maskId) {
              setMaskId(r.maskId);
              const rm = MASKS.find((x) => x.id === r.maskId);
              if (rm && fitRef.current) fitMask(rm); // автоподгонка восстановленной маски под текущий экран
            }
            if (r.color) setColor(r.color);
            if (typeof r.intensity === "number") setIntensity(r.intensity);
            if (typeof r.dashed === "boolean") setDashed(r.dashed);
            if (r.analysis) setAnalysis(r.analysis);
            if (r.haircare) setHaircare(r.haircare);
            if (r.lookImageId) {
              setLookImage(`${API}/images/${r.lookImageId}`);
              setLookTitle(r.lookTitle || "");
            }
            if (typeof r.tryOnRemaining === "number") setTryOnRemaining(r.tryOnRemaining);
            applyRestoredImages(r);
            resumePendingJobs(r);
            if (r.view && r.view !== "editor") setView(r.view); // возвращаем на сохранённый экран
          }
        } else {
          setFaceFound(false);
          setPose({ cx: offX + rendW / 2, cy: offY + rendH * 0.42, w: rendW * 0.85, rot: 0 });
        }
      } catch (err) {
        if (!cancelled) {
          setFaceFound(false);
          setDetectError(String(err?.message || err || "unknown"));
          const img = imgRef.current;
          if (img) setPose({ cx: img.clientWidth / 2, cy: img.clientHeight * 0.42, w: img.clientWidth * 0.85, rot: 0 });
        }
      } finally {
        if (!cancelled) setStatus("ready");
      }
    };
    run();
    return () => { cancelled = true; };
  }, [photoUrl]);

  useEffect(() => {
    const handle = maskHandleRef.current;
    if (!handle) return;
    const onWheel = (e) => {
      e.preventDefault();
      setPose((p) => ({ ...p, w: clampW(p.w * (e.deltaY > 0 ? 0.93 : 1.075)) }));
    };
    handle.addEventListener("wheel", onWheel, { passive: false });
    return () => handle.removeEventListener("wheel", onWheel);
  }, [status]);

  const startGesture = (e, allowDrag) => {
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointersRef.current.size === 2) {
      const [a, b] = [...pointersRef.current.values()];
      dragRef.current = {
        mode: "pinch",
        dist: Math.hypot(a.x - b.x, a.y - b.y),
        angle: Math.atan2(b.y - a.y, b.x - a.x),
        startW: poseRef.current.w,
        startRot: poseRef.current.rot,
      };
    } else if (allowDrag) {
      dragRef.current = { mode: "drag", startX: e.clientX, startY: e.clientY, start: { ...poseRef.current } };
    }
  };

  const onPointerMove = (e) => {
    if (!pointersRef.current.has(e.pointerId) || !dragRef.current) return;
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const drag = dragRef.current;
    if (drag.mode === "pinch" && pointersRef.current.size === 2) {
      const [a, b] = [...pointersRef.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const angle = Math.atan2(b.y - a.y, b.x - a.x);
      setPose((p) => ({
        ...p,
        w: dist > 0 ? clampW(drag.startW * (dist / drag.dist)) : p.w,
        rot: drag.startRot + (angle - drag.angle),
      }));
    } else if (drag.mode === "drag") {
      setPose((p) => ({
        ...p,
        cx: drag.start.cx + (e.clientX - drag.startX),
        cy: drag.start.cy + (e.clientY - drag.startY),
      }));
    }
  };

  const endPointer = (e) => {
    pointersRef.current.delete(e.pointerId);
    if (pointersRef.current.size === 1) {
      const [remaining] = [...pointersRef.current.values()];
      dragRef.current = { mode: "drag", startX: remaining.x, startY: remaining.y, start: { ...poseRef.current } };
    } else if (pointersRef.current.size === 0) {
      dragRef.current = null;
    }
  };

  const runAnalysis = async () => {
    setAnalyzing(true);
    setAnalysisTab("analysis");
    setRecFilter("all");
    setOpenZone(null);
    setAnalysisError("");
    try {
      const smallImage = await downscaleForAnalysis(photoUrl);
      const headers = authHeaders();
      // При сетевом обрыве на загрузке (нет ответа сервера) — одна тихая повторная попытка
      let data = null;
      let lastErr = null;
      let jobId = null;
      for (let attempt = 0; attempt < 2 && !data; attempt++) {
        try {
          const { data: job } = await axios.post(`${API}/analyze-face`, { image: smallImage, metrics: metricsRef.current }, { timeout: 60000, headers });
          if (jobId) removePendingJob(jobId); // ретрай создал новую задачу — старую из сессии убираем
          jobId = job.job_id;
          addPendingJob({ id: job.job_id, kind: "analyze" });
          data = await pollJob(job.job_id, headers);
        } catch (err) {
          lastErr = err;
          if (err?.response) break; // сервер ответил осмысленно — повторять бессмысленно
        }
      }
      if (jobId) removePendingJob(jobId);
      if (!data) throw lastErr;
      if (data && typeof data === "object" && data.face_shape) {
        setAnalysis(data);
        saveSession({ analysis: data });
        flushSession();
      } else {
        setAnalysisError("ИИ вернул неожиданный ответ.");
      }
    } catch (err) {
      const raw = err.response?.data;
      if (CF_STATUS.has(err.response?.status) || (typeof raw === "string" && /cloudflare|origin web server/i.test(raw))) {
        setAnalysisError("Сервер перезапускается или перегружен. Подождите 30–60 секунд и нажмите «Повторить разбор» — фото никуда не денется.");
      } else {
        setAnalysisError(
          typeof err.response?.data?.detail === "string"
            ? err.response.data.detail
            : "Разбор не удался. Проверьте соединение и попробуйте ещё раз."
        );
      }
    } finally {
      setAnalyzing(false);
    }
  };

  const showTryOnResult = (image, recName) => {
    setTryOnImage(image);
    setTryOnRec(recName);
    setTechCard(null);
    setTechCardIdx(null);
    setShowBefore(false);
    setAnalysisTab("styles");
    saveSession({ tryOnRec: recName });
    setTimeout(() => document.querySelector('[data-testid="analysis-screen"]')?.scrollTo({ top: 0, behavior: "smooth" }), 120);
  };

  const runTryOn = async (rec, i) => {
    const cached = triedMap[rec.name];
    if (cached) {
      showTryOnResult(cached, rec.name);
      return;
    }
    setTryOnLoading(i);
    setTryOnImage(null);
    setTechCard(null);
    setTechCardIdx(null);
    setShowBefore(false);
    let job = null;
    try {
      const headers = authHeaders();
      job = (await axios.post(
        `${API}/try-on`,
        { image: photoUrl, prompt: rec.style || rec.name, client_key: getClientKey(), phone: getPhone(), name: rec.name || "", score: analysis?.score || analysis?.overall_score || 0, face_shape: analysis?.face_shape || "" },
        { timeout: 60000, headers }
      )).data;
      addPendingJob({ id: job.job_id, kind: "tryon", recName: rec.name });
      const data = await pollJob(job.job_id, headers);
      removePendingJob(job.job_id);
      setTriedMap((m) => ({ ...m, [rec.name]: data.image }));
      showTryOnResult(data.image, rec.name);
      if (typeof data.remaining === "number") setTryOnRemaining(data.remaining);
      const cur = loadSession() || {};
      saveSession({
        triedIds: { ...(cur.triedIds || {}), [rec.name]: data.image_id },
        tryOnRec: rec.name,
        ...(typeof data.remaining === "number" ? { tryOnRemaining: data.remaining } : {}),
      });
      flushSession();
      toast.success("Примерка готова");
    } catch (err) {
      // Таймаут опроса — генерация может ещё завершиться: задачу оставляем в сессии
      if (job && err?.response?.status !== 504) removePendingJob(job.job_id);
      notifyTryOnError(err);
    } finally {
      setTryOnLoading(-1);
    }
  };

  // ——— Полный лук: одежда поверх примеренной стрижки ———
  const openLookPanel = async () => {
    setLookOpen(true);
    if (lookItems === null) {
      try {
        const { data } = await axios.get(`${API}/wardrobe/items`, { headers: authHeaders() });
        setLookItems(data.items || []);
      } catch {
        setLookItems([]);
      }
    }
  };

  const previewLook = async (mode) => {
    if (mode === "wardrobe" && (lookItems || []).length < 2) {
      toast("Гардероб почти пуст", {
        description: "Добавьте вещи — кнопка «Гардероб» на стартовом экране. Стилист соберёт образ из них.",
      });
      return;
    }
    setLookLoading(true);
    setLookPreview(null);
    try {
      const headers = authHeaders();
      if (mode === "wardrobe") {
        const job = (await axios.post(
          `${API}/wardrobe/outfits`,
          { occasion: `под стрижку «${tryOnRec}»` },
          { timeout: 60000, headers }
        )).data;
        const res = await pollJob(job.job_id, headers);
        const o = (res.outfits || [])[0];
        if (!o || !o.clothes_en) {
          throw { response: { data: { detail: "Из вещей гардероба образ не собрался — добавьте ещё пару вещей." } } };
        }
        setLookPreview({ mode, title: o.title, why: o.why, clothes_en: o.clothes_en, item_ids: o.item_ids || [] });
      } else {
        const job = (await axios.post(
          `${API}/look/recommend`,
          { image: tryOnImage, hairstyle: tryOnRec, client_key: getClientKey() },
          { timeout: 60000, headers }
        )).data;
        const res = await pollJob(job.job_id, headers);
        if (!res?.clothes_en) {
          throw { response: { data: { detail: "Стилист не ответил. Попробуйте ещё раз." } } };
        }
        setLookPreview({ mode, title: res.title, why: res.description, clothes_en: res.clothes_en, item_ids: [] });
      }
    } catch (err) {
      toast.error(
        typeof err?.response?.data?.detail === "string"
          ? err.response.data.detail
          : "Стилист не ответил. Попробуйте ещё раз."
      );
    } finally {
      setLookLoading(false);
    }
  };

  const applyLook = async () => {
    if (!lookPreview?.clothes_en) return;
    setLookGenerating(true);
    let job = null;
    try {
      const headers = authHeaders();
      job = (await axios.post(
        `${API}/look/tryon`,
        {
          image: tryOnImage,
          clothes_en: lookPreview.clothes_en,
          title: lookPreview.title,
          item_ids: lookPreview.item_ids,
          hairstyle: tryOnRec,
          client_key: getClientKey(),
          phone: getPhone(),
          score: analysis?.score || analysis?.overall_score || 0,
          face_shape: analysis?.face_shape || "",
        },
        { timeout: 60000, headers }
      )).data;
      addPendingJob({ id: job.job_id, kind: "look", lookTitle: lookPreview.title });
      const data = await pollJob(job.job_id, headers);
      removePendingJob(job.job_id);
      setLookImage(data.image);
      setLookTitle(lookPreview.title);
      setLookOpen(false);
      setLookPreview(null);
      if (typeof data.remaining === "number") setTryOnRemaining(data.remaining);
      saveSession({
        lookImageId: data.image_id,
        lookTitle: lookPreview.title,
        ...(typeof data.remaining === "number" ? { tryOnRemaining: data.remaining } : {}),
      });
      flushSession();
      toast.success("Образ готов");
    } catch (err) {
      // Таймаут опроса — генерация может ещё завершиться: задачу оставляем в сессии
      if (job && err?.response?.status !== 504) removePendingJob(job.job_id);
      notifyTryOnError(err);
    } finally {
      setLookGenerating(false);
    }
  };

  const runCare = async () => {
    setCareLoading(true);
    let job = null;
    try {
      const headers = authHeaders();
      job = (await axios.post(
        `${API}/haircare`,
        { image: photoUrl, client_key: getClientKey(), phone: getPhone() },
        { timeout: 60000, headers }
      )).data;
      addPendingJob({ id: job.job_id, kind: "haircare" });
      const data = await pollJob(job.job_id, headers);
      removePendingJob(job.job_id);
      setHaircare(data);
      saveSession({
        haircare: data,
        ...(typeof data.remaining === "number" ? { tryOnRemaining: data.remaining } : {}),
      });
      flushSession();
      if (typeof data.remaining === "number") setTryOnRemaining(data.remaining);
      toast.success("Подбор ухода готов");
    } catch (err) {
      if (job && err?.response?.status !== 504) removePendingJob(job.job_id);
      notifyTryOnError(err);
    } finally {
      setCareLoading(false);
    }
  };

  const saveCareCard = () => {
    // Карточка уже оплачена квотой — сохраняем без тарифного гейта
    const url = `${API}/images/${haircare.image_id}`;
    const tg = window.Telegram?.WebApp;
    const canNativeDownload = Boolean(
      tg?.initData && typeof tg.isVersionAtLeast === "function" && tg.isVersionAtLeast("8.0") && tg.downloadFile
    );
    if (canNativeDownload) {
      tg.downloadFile({ url, file_name: "dizayn-pro-care.jpg" });
      toast.success("Сохраняем карточку — проверьте загрузки");
      return;
    }
    const a = document.createElement("a");
    a.href = url;
    a.download = "dizayn-pro-care.jpg";
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("Карточка сохранена");
  };

  const loadImage = (src) =>
    new Promise((resolve, reject) => {
      const im = new Image();
      im.onload = () => resolve(im);
      im.onerror = reject;
      im.src = src;
    });

  const saveGateToast = () =>
    toast("Сохранение доступно по тарифу", {
      description: "Оформите тариф в личном кабинете — откроется сохранение фото с маской и примерок.",
      action: { label: "Тарифы", onClick: () => window.location.assign("/account") },
    });

  // Скачивание: в Telegram WebView <a download> молча игнорируется —
  // там сохраняем файл на сервер и вызываем нативный Telegram.WebApp.downloadFile.
  // Если и он недоступен — показываем оверлей «зажмите и сохраните».
  const saveImageFile = async (dataUrl, fileName, okMsg) => {
    const tg = window.Telegram?.WebApp;
    if (tg?.initData) {
      // downloadFile — с Bot API 8.0; на старых клиентах метод может быть заглушкой
      const canNativeDownload =
        typeof tg.isVersionAtLeast === "function" && tg.isVersionAtLeast("8.0") && tg.downloadFile;
      if (canNativeDownload) {
        try {
          const { data } = await axios.post(`${API}/render`, { image: dataUrl }, { timeout: 60000 });
          tg.downloadFile({
            url: `${BACKEND_URL}/api/render/${data.id}`,
            file_name: fileName,
          });
          toast.success(okMsg);
          return;
        } catch {
          // ниже — ручное сохранение
        }
      }
      setSavePreview(dataUrl);
      return;
    }
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = fileName;
    a.click();
    toast.success(okMsg);
  };

  const handleDownload = async () => {
    if (!canSave) {
      saveGateToast();
      return;
    }
    try {
      const stage = stageRef.current;
      if (!stage) return;
      const img = await loadImage(photoUrl);
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      // pose задан в координатах сцены (object-contain) — переводим в пиксели исходного фото
      const sw = stage.clientWidth;
      const sh = stage.clientHeight;
      const s = Math.min(sw / img.naturalWidth, sh / img.naturalHeight);
      const ox = (sw - img.naturalWidth * s) / 2;
      const oy = (sh - img.naturalHeight * s) / 2;
      const k = 1 / s;
      if (tintedSrc) {
        const maskImg = await loadImage(tintedSrc);
        const w = pose.w * k;
        const h = maskH * k;
        ctx.save();
        ctx.translate((pose.cx - ox) * k, (pose.cy - oy) * k);
        ctx.rotate(pose.rot);
        ctx.globalAlpha = intensity;
        ctx.drawImage(maskImg, w * (0.5 - mask.ax) - w / 2, h * (0.5 - mask.ay) - h / 2, w, h);
        ctx.restore();
      }
      if (showLines && lines) {
        ctx.save();
        ctx.strokeStyle = "rgba(194,168,120,0.55)";
        ctx.fillStyle = "rgba(194,168,120,0.85)";
        ctx.lineWidth = Math.max(1, Math.round(k));
        ctx.setLineDash([4 * k, 6 * k]);
        LINE_DEFS.forEach((d) => {
          const y = (lines[d.key] - oy) * k;
          ctx.beginPath();
          ctx.moveTo(0, y);
          ctx.lineTo(canvas.width, y);
          ctx.stroke();
          ctx.font = `${Math.max(10, Math.round(9 * k))}px monospace`;
          ctx.fillText(d.label.toUpperCase(), 8 * k, y - 6 * k);
        });
        ctx.strokeStyle = "rgba(194,168,120,0.4)";
        const cx = (lines.centerX - ox) * k;
        ctx.beginPath();
        ctx.moveTo(cx, 0);
        ctx.lineTo(cx, canvas.height);
        ctx.stroke();
        ctx.restore();
      }
      await saveImageFile(canvas.toDataURL("image/png"), "dizayn-pro.png", "Фото с маской сохранено");
    } catch {
      toast.error("Не удалось сохранить. Попробуйте ещё раз.");
    }
  };

  const handleSaveTryOn = async () => {
    if (!canSave) {
      saveGateToast();
      return;
    }
    const ext = tryOnImage.startsWith("data:image/png") ? "png" : "jpg";
    await saveImageFile(tryOnImage, `dizayn-pro-tryon.${ext}`, "Примерка сохранена");
  };

  // Доступ мастера и сохранение проверяем при входе на экран разбора и когда фото готово
  useEffect(() => {
    if (view !== "analysis" && status !== "ready") return;
    axios
      .get(`${API}/master-access`, { params: { client_key: getClientKey(), phone: getPhone() }, headers: authHeaders() })
      .then(({ data }) => {
        setIsMaster(!!data.master);
        setCanSave(!!data.can_save);
      })
      .catch(() => {});
  }, [view, status]);

  const normalizeRef = (file) =>
    new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        const maxSide = 1024;
        const k = Math.min(1, maxSide / Math.max(img.naturalWidth, img.naturalHeight));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.naturalWidth * k);
        canvas.height = Math.round(img.naturalHeight * k);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        URL.revokeObjectURL(url);
        resolve(canvas.toDataURL("image/jpeg", 0.9));
      };
      img.onerror = reject;
      img.src = url;
    });

  const handleRefFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      setRefImage(await normalizeRef(file));
    } catch {
      toast.error("Не удалось прочитать фото референса.");
    }
  };

  const runRefTryOn = async () => {
    if (!refImage || refLoading) return;
    setRefLoading(true);
    setTryOnImage(null);
    setTechCard(null);
    setTechCardIdx(null);
    setShowBefore(false);
    setTryOnRec("Стрижка по вашему референсу");
    let job = null;
    try {
      const headers = authHeaders();
      job = (await axios.post(
        `${API}/try-on-ref`,
        { image: photoUrl, reference_image: refImage, client_key: getClientKey(), phone: getPhone(), score: analysis?.score || analysis?.overall_score || 0, face_shape: analysis?.face_shape || "" },
        { timeout: 60000, headers }
      )).data;
      addPendingJob({ id: job.job_id, kind: "tryon", recName: "Стрижка по вашему референсу" });
      const data = await pollJob(job.job_id, headers);
      removePendingJob(job.job_id);
      setTryOnImage(data.image);
      if (typeof data.remaining === "number") setTryOnRemaining(data.remaining);
      const cur = loadSession() || {};
      saveSession({
        triedIds: { ...(cur.triedIds || {}), "Стрижка по вашему референсу": data.image_id },
        tryOnRec: "Стрижка по вашему референсу",
        ...(typeof data.remaining === "number" ? { tryOnRemaining: data.remaining } : {}),
      });
      flushSession();
      setAnalysisTab("styles");
      setTimeout(() => document.querySelector('[data-testid="analysis-screen"]')?.scrollTo({ top: 0, behavior: "smooth" }), 120);
      toast.success("Примерка готова");
    } catch (err) {
      if (job && err?.response?.status !== 504) removePendingJob(job.job_id);
      notifyTryOnError(err);
    } finally {
      setRefLoading(false);
    }
  };

  const runTechCard = async (rec, idx) => {
    if (techLoading) return;
    if (!isMaster) {
      toast("Техкарта — платная функция", {
        description: "Входит в тариф «Про» — оформите в личном кабинете.",
        action: { label: "Тарифы", onClick: () => window.location.assign("/account") },
      });
      return;
    }
    if (!tryOnImage) {
      toast("Сначала сделайте примерку", {
        description: "Техкарта строится по фото готового результата.",
      });
      return;
    }
    setTechCardIdx(idx);
    setTechLoading(true);
    setTechCard(null);
    try {
      const { data } = await axios.post(
        `${API}/master/tech-card`,
        {
          image: tryOnImage,
          rec: rec.name,
          face_shape: analysis?.face_shape || "",
          client_key: getClientKey(),
          phone: getPhone(),
        },
        { timeout: 120000, headers: authHeaders() }
      );
      setTechCard(data.card);
      toast.success("Техкарта готова");
    } catch (err) {
      toast.error(
        typeof err.response?.data?.detail === "string"
          ? err.response.data.detail
          : "Не удалось составить техкарту. Попробуйте позже."
      );
    } finally {
      setTechLoading(false);
    }
  };

  const lines = linesRef.current;
  const maskH = pose.w * mask.aspect;

  const actionBtn =
    "flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-[#0F0F0F]/60 text-[#F4F4F0] backdrop-blur-xl transition-colors duration-300 hover:border-[#C2A878]/60 hover:text-[#C2A878] disabled:cursor-not-allowed disabled:opacity-40";
  const actionLabel =
    "font-jetbrains text-[8px] uppercase tracking-[0.2em] text-[#F4F4F0]/70";

  return (
    <div data-testid="mask-editor">
      {view === "editor" && (
      <div
        ref={stageRef}
        onPointerDown={(e) => startGesture(e, false)}
        onPointerMove={onPointerMove}
        onPointerUp={endPointer}
        onPointerCancel={endPointer}
        className="relative block w-full select-none overflow-hidden bg-[#0A0A0A]"
        style={{ height: "100dvh", touchAction: "none", overscrollBehavior: "none" }}
        data-testid="mask-stage"
      >
        <img
          src={photoUrl}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 z-0 h-full w-full scale-110 object-cover opacity-60 blur-2xl"
          draggable={false}
        />
        <img
          ref={imgRef}
          src={photoUrl}
          alt="Ваше фото"
          className="relative block h-full w-full object-contain"
          draggable={false}
        />

        <div
          className="absolute left-0 top-0"
          style={{
            transform: `translate(${pose.cx}px, ${pose.cy}px) rotate(${pose.rot}rad)`,
            transition: fitAnim ? "transform 0.45s cubic-bezier(0.16, 1, 0.3, 1)" : "none",
          }}
        >
          <div
            ref={maskHandleRef}
            onPointerDown={(e) => startGesture(e, true)}
            onPointerMove={onPointerMove}
            onPointerUp={endPointer}
            onPointerCancel={endPointer}
            className="absolute cursor-grab active:cursor-grabbing"
            style={{
              left: pose.w * (0.5 - mask.ax) - pose.w / 2,
              top: maskH * (0.5 - mask.ay) - maskH / 2,
              width: pose.w,
              height: maskH,
              touchAction: "none",
            }}
            data-testid="mask-handle"
          >
            {tintedSrc && (
              <img
                src={tintedSrc}
                alt=""
                draggable={false}
                className="pointer-events-none absolute left-0 top-0 max-w-none select-none"
                style={{
                  width: pose.w,
                  height: maskH,
                  opacity: intensity,
                  filter: `drop-shadow(0 0 ${Math.round(14 * intensity)}px ${color}66)`,
                }}
                data-testid="mask-element"
              />
            )}
          </div>
        </div>

        {showLines && lines && (
          <svg className="pointer-events-none absolute inset-0 h-full w-full" data-testid="ratio-lines-overlay">
            {LINE_DEFS.map((d) => (
              <g key={d.key}>
                <line x1="0" x2="100%" y1={lines[d.key]} y2={lines[d.key]} stroke="#C2A878" strokeOpacity="0.55" strokeWidth="1" strokeDasharray="4 6" />
                <text x="8" y={lines[d.key] - 6} fill="#C2A878" fillOpacity="0.85" fontSize="9" fontFamily="JetBrains Mono, monospace" letterSpacing="2">
                  {d.label.toUpperCase()}
                </text>
              </g>
            ))}
            <line x1={lines.centerX} x2={lines.centerX} y1="0" y2="100%" stroke="#C2A878" strokeOpacity="0.4" strokeWidth="1" strokeDasharray="4 6" />
          </svg>
        )}

        {status === "ready" && faceFound && (
          <span className="sr-only" data-testid="mask-hint">Маска готова</span>
        )}

        {status === "ready" && !faceFound && (
          <div
            className="absolute inset-x-4 bottom-24 rounded-2xl border border-white/10 bg-[#0A0A0A]/80 p-4 text-center backdrop-blur-xl"
            data-testid="mask-no-face"
          >
            <p className="text-sm text-[#C2A878]">
              Не получилось распознать лицо — попробуйте фото анфас, где лицо крупнее.
            </p>
            {detectError && (
              <p className="mt-2 break-all font-jetbrains text-[10px] leading-relaxed text-[#525252]" data-testid="mask-detect-error">
                {detectError}
              </p>
            )}
          </div>
        )}

        {status === "ready" && (
          <div
            className="absolute bottom-24 right-3 z-10 flex flex-col items-center gap-3"
            onPointerDown={(e) => e.stopPropagation()}
            data-testid="mask-actions"
          >
            <div className="flex flex-col items-center gap-1">
              <button
                onClick={() => {
                  setView("analysis");
                  if (!analysis && !analyzing) runAnalysis();
                }}
                disabled={analyzing}
                aria-label="ИИ-анализ"
                className="flex h-[54px] w-[54px] items-center justify-center rounded-full bg-[#C2A878] text-[#0A0A0A] shadow-[0_8px_24px_rgba(194,168,120,0.35)] transition-transform duration-300 hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50"
                data-testid="ai-analyze-button"
              >
                {analyzing ? (
                  <Loader2 size={20} className="animate-spin" />
                ) : (
                  <Sparkles size={20} strokeWidth={1.5} />
                )}
              </button>
              <span className={actionLabel} style={{ textShadow: "0 1px 6px rgba(0,0,0,0.8)" }}>
                AI разбор
              </span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <button
                onClick={() => setShowStyle((v) => !v)}
                aria-label="Настройки маски"
                className={`${actionBtn} ${showStyle ? "border-[#C2A878] text-[#C2A878]" : ""}`}
                data-testid="style-sheet-button"
              >
                <SlidersHorizontal size={17} strokeWidth={1.5} />
              </button>
              <span className={actionLabel} style={{ textShadow: "0 1px 6px rgba(0,0,0,0.8)" }}>
                Настройки
              </span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <button
                onClick={() => setShowLines((v) => !v)}
                disabled={!faceFound}
                aria-label="Линии пропорций"
                className={`${actionBtn} ${showLines ? "border-[#C2A878] text-[#C2A878]" : ""}`}
                data-testid="toggle-lines-button"
              >
                <Ruler size={17} strokeWidth={1.5} />
              </button>
              <span className={actionLabel} style={{ textShadow: "0 1px 6px rgba(0,0,0,0.8)" }}>
                Линии
              </span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <button
                onClick={handleDownload}
                aria-label="Скачать результат"
                className={actionBtn}
                data-testid="download-result-button"
              >
                <Download size={17} strokeWidth={1.5} />
              </button>
              <span className={actionLabel} style={{ textShadow: "0 1px 6px rgba(0,0,0,0.8)" }}>
                Скачать
              </span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <button
                onClick={onNewPhoto}
                aria-label="Другое фото"
                className={actionBtn}
                data-testid="new-photo-button"
              >
                <ImagePlus size={17} strokeWidth={1.5} />
              </button>
              <span className={`${actionLabel} whitespace-nowrap`} style={{ textShadow: "0 1px 6px rgba(0,0,0,0.8)" }}>
                Сменить фото
              </span>
            </div>
          </div>
        )}

        {status === "ready" && (
          <div
            className="absolute bottom-4 left-3 right-[72px] rounded-[28px] border border-white/10 bg-[#0F0F0F]/55 p-2 backdrop-blur-xl"
            onPointerDown={(e) => e.stopPropagation()}
            data-testid="mask-selector"
          >
            <div className="flex gap-2 overflow-x-auto" style={{ scrollbarWidth: "none", touchAction: "pan-x" }}>
              {MASKS.map((m) => (
                <button
                  key={m.id}
                  onClick={() => selectMask(m.id)}
                  aria-label={m.name}
                  className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border transition-colors duration-300 ${
                    maskId === m.id
                      ? "border-[#C2A878] bg-[#C2A878]/15"
                      : "border-white/10 hover:border-[#C2A878]/50"
                  }`}
                  data-testid={`mask-variant-${m.id}`}
                >
                  <img
                    src={m.src}
                    alt={m.name}
                    className="h-7 w-7 object-contain"
                    style={{ filter: "brightness(0) invert(0.85)" }}
                    draggable={false}
                  />
                </button>
              ))}
            </div>
          </div>
        )}

        {showStyle && status === "ready" && (
            <>
              <div
                className="fade-in absolute inset-0 z-20"
                onPointerDown={(e) => {
                  e.stopPropagation();
                  setShowStyle(false);
                }}
                data-testid="style-sheet-backdrop"
              />
              <div
                className="sheet-up absolute inset-x-0 bottom-0 z-30 rounded-t-[28px] border-t border-white/10 bg-[#0E0E0E]/85 px-6 pb-8 pt-3 backdrop-blur-2xl"
                onPointerDown={(e) => e.stopPropagation()}
                data-testid="mask-style-controls"
              >
                <button
                  onClick={() => setShowStyle(false)}
                  aria-label="Закрыть панель стиля"
                  className="mx-auto mb-5 block h-1 w-10 rounded-full bg-white/20"
                  data-testid="style-sheet-close"
                />
                <div className="flex items-center gap-3" data-testid="mask-color-picker">
                  <span className="w-24 shrink-0 font-jetbrains text-[10px] uppercase tracking-[0.2em] text-[#525252]">
                    Цвет
                  </span>
                  {MASK_COLORS.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => setColor(c.hex)}
                      aria-label={c.name}
                      className={`h-7 w-7 shrink-0 rounded-full transition-transform duration-300 hover:scale-110 ${
                        color === c.hex
                          ? "ring-2 ring-[#C2A878] ring-offset-2 ring-offset-[#0A0A0A]"
                          : "ring-1 ring-white/20"
                      }`}
                      style={{ backgroundColor: c.hex }}
                      data-testid={`mask-color-${c.id}`}
                    />
                  ))}
                </div>
                <div className="mt-5 flex items-center gap-3">
                  <span className="w-24 shrink-0 font-jetbrains text-[10px] uppercase tracking-[0.2em] text-[#525252]">
                    Линии
                  </span>
                  <button
                    onClick={() => setDashed(false)}
                    disabled={!mask.dash}
                    className={`flex items-center gap-2 rounded-full border px-4 py-2 font-jetbrains text-[10px] uppercase tracking-[0.15em] transition-colors duration-300 disabled:cursor-not-allowed disabled:opacity-30 ${
                      !dashed
                        ? "border-[#C2A878] bg-[#C2A878]/10 text-[#C2A878]"
                        : "border-white/10 text-[#A3A3A3] hover:border-[#C2A878]/50 hover:text-[#C2A878]"
                    }`}
                    data-testid="mask-solid-chip"
                  >
                    <Minus size={13} strokeWidth={1.5} />
                    Сплошная
                  </button>
                  <button
                    onClick={() => setDashed(true)}
                    disabled={!mask.dash}
                    className={`flex items-center gap-2 rounded-full border px-4 py-2 font-jetbrains text-[10px] uppercase tracking-[0.15em] transition-colors duration-300 disabled:cursor-not-allowed disabled:opacity-30 ${
                      dashed
                        ? "border-[#C2A878] bg-[#C2A878]/10 text-[#C2A878]"
                        : "border-white/10 text-[#A3A3A3] hover:border-[#C2A878]/50 hover:text-[#C2A878]"
                    }`}
                    data-testid="mask-dash-toggle"
                  >
                    <MoreHorizontal size={13} strokeWidth={1.5} />
                    Пунктир
                  </button>
                </div>
                <div className="mt-5 flex items-center gap-4">
                  <span className="w-24 shrink-0 font-jetbrains text-[10px] uppercase tracking-[0.2em] text-[#525252]">
                    Интенсивность
                  </span>
                  <input
                    type="range"
                    min="15"
                    max="100"
                    value={Math.round(intensity * 100)}
                    onChange={(e) => setIntensity(Number(e.target.value) / 100)}
                    className="w-full accent-[#C2A878]"
                    data-testid="mask-intensity-slider"
                  />
                </div>
              </div>
            </>
          )}

        {status !== "ready" && (
          willRestore ? (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-[#0A0A0A]" data-testid="mask-status">
              <Loader2 size={28} className="animate-spin text-[#C2A878]" />
            </div>
          ) : (
            <AnalysisScan
              steps={status === "models" ? DETECT_STEPS.models : DETECT_STEPS.detect}
              note="Первый запуск может занять до минуты"
              fill
              testid="mask-status"
            />
          )
        )}
      </div>
      )}

      {savePreview && (
        <div
          className="fixed inset-0 z-[70] flex flex-col bg-[#0A0A0A]/95 px-6 pb-8"
          style={{ paddingTop: 24 + topInset }}
          data-testid="save-preview-overlay"
        >
          <p className="py-4 text-center font-jetbrains text-[10px] uppercase leading-relaxed tracking-[0.25em] text-[#C2A878]">
            Зажмите фото и выберите «Сохранить изображение»
          </p>
          <img
            src={savePreview}
            alt="Фото для сохранения"
            className="min-h-0 w-full flex-1 object-contain"
            data-testid="save-preview-image"
          />
          <button
            onClick={() => setSavePreview(null)}
            className="mx-auto mt-5 border border-white/15 px-10 py-3.5 font-jetbrains text-[10px] uppercase tracking-[0.25em] text-[#A3A3A3] transition-colors duration-300 hover:border-[#C2A878]/60 hover:text-[#C2A878]"
            data-testid="save-preview-close"
          >
            Закрыть
          </button>
        </div>
      )}

      {view === "analysis" && (
        <div
          className={`h-dvh overflow-y-auto pb-24${restoredUi ? " restored-ui" : ""}`}
          style={{ paddingTop: 96 + topInset }}
          data-testid="analysis-screen"
        >
          {/* Еле видимая фактура лица + золотые линии — как на лендинге */}
          <FaceTexture />
          <div className="relative flex items-center justify-between border-b border-[#C2A878]/10 px-6 pb-5">
            <button
              onClick={() => setView("editor")}
              className="flex items-center gap-2 font-jetbrains text-xs uppercase tracking-[0.25em] text-[#C2A878] opacity-80 transition-opacity duration-300 hover:opacity-100"
              data-testid="back-to-editor-button"
            >
              <ArrowLeft size={14} strokeWidth={1.5} />
              К маскам
            </button>
            <span className="font-jetbrains text-[10px] uppercase tracking-[0.3em] text-white/40">
              ( Разбор ИИ )
            </span>
          </div>
          {analyzing && !analysis && <AnalysisScan photoUrl={photoUrl} note="Фото один раз отправляется в ИИ" />}
          {!analyzing && !analysis && (
            <div className="relative flex flex-col items-center px-6 py-20 text-center" data-testid="analysis-error">
              <p className="font-cormorant text-2xl font-light text-[#F4F4F0]">Разбор не завершился</p>
              <p className="mt-3 max-w-xs text-sm leading-relaxed text-[#A3A3A3]">
                {analysisError || "Ответ ИИ не дошёл. Попробуйте ещё раз — фото никуда не денется."}
              </p>
              <button
                onClick={runAnalysis}
                className="mt-8 flex items-center gap-3 border border-[#C2A878] px-8 py-4 font-jetbrains text-xs uppercase tracking-[0.25em] text-[#C2A878] transition-colors duration-500 hover:bg-[#C2A878] hover:text-[#0A0A0A]"
                data-testid="analysis-retry-button"
              >
                Повторить разбор
              </button>
            </div>
          )}

      {analysis && (
        <div
          ref={resultRef}
          className="relative w-full"
          data-testid="ai-result-panel"
        >
          <div className="flex gap-2 px-6 pt-5" data-testid="analysis-tabs">
            <button
              onClick={() => setAnalysisTab("analysis")}
              className={`border px-5 py-2.5 font-jetbrains text-[10px] uppercase tracking-[0.25em] transition-colors duration-300 ${
                analysisTab === "analysis"
                  ? "border-[#C2A878] bg-[#C2A878]/10 text-[#C2A878]"
                  : "border-white/15 text-[#A3A3A3] hover:border-[#C2A878]/50 hover:text-[#F4F4F0]"
              }`}
              data-testid="analysis-tab-analysis"
            >
              Разбор
            </button>
            <button
              onClick={() => setAnalysisTab("styles")}
              className={`relative border px-5 py-2.5 font-jetbrains text-[10px] uppercase tracking-[0.25em] transition-colors duration-300 ${
                analysisTab === "styles"
                  ? "border-[#C2A878] bg-[#C2A878]/10 text-[#C2A878]"
                  : "border-white/15 text-[#A3A3A3] hover:border-[#C2A878]/50 hover:text-[#F4F4F0]"
              }`}
              data-testid="analysis-tab-styles"
            >
              Стрижки
              {tryOnImage && analysisTab !== "styles" && (
                <span className="absolute -right-1 -top-1 h-1.5 w-1.5 rounded-full bg-[#C2A878]" />
              )}
            </button>
            <button
              onClick={() => setAnalysisTab("care")}
              className={`relative border px-5 py-2.5 font-jetbrains text-[10px] uppercase tracking-[0.25em] transition-colors duration-300 ${
                analysisTab === "care"
                  ? "border-[#C2A878] bg-[#C2A878]/10 text-[#C2A878]"
                  : "border-white/15 text-[#A3A3A3] hover:border-[#C2A878]/50 hover:text-[#F4F4F0]"
              }`}
              data-testid="analysis-tab-care"
            >
              Уход
            </button>
          </div>

          {analysisTab === "analysis" && (
          <>
          <div className="relative px-6 pb-6 pt-5">
            <FaceShapeCard
              shape={analysis.face_shape}
              desc={analysis.face_shape_desc}
              score={analysis.score}
              features={analysis.features}
              instant={restoredUi}
            />

            <button
              onClick={() => {
                setAnalysisTab("styles");
                document.querySelector('[data-testid="analysis-screen"]')?.scrollTo({ top: 0, behavior: "smooth" });
              }}
              className="rise-in mt-5 flex w-full items-center justify-center gap-3 bg-[#C2A878] py-4 font-jetbrains text-xs uppercase tracking-[0.25em] text-[#0A0A0A] transition-colors duration-500 hover:bg-[#D4BC8A]"
              style={{ animationDelay: "0.14s" }}
              data-testid="go-styles-button"
            >
              Смотреть стрижки
              <ArrowRight size={14} strokeWidth={1.5} />
            </button>

            <p className="mt-7 font-jetbrains text-[10px] uppercase tracking-[0.3em] text-[#525252]">
              ( Пропорции по зонам )
            </p>
          </div>

          <div className="px-6" data-testid="ai-zones">
            {(analysis.zones || []).map((z, i) => (
              <div
                key={z.name}
                className="rise-in border-t border-white/10 last:border-b"
                style={{ animationDelay: `${0.2 + i * 0.05}s` }}
              >
                <button
                  onClick={() => setOpenZone(openZone === i ? null : i)}
                  className="flex w-full items-center gap-3 py-3.5 text-left"
                  data-testid={`ai-zone-${i}`}
                >
                  <span className="font-jetbrains text-[10px] tracking-[0.2em] text-[#525252]">
                    0{i + 1}
                  </span>
                  <span className="font-cormorant text-lg font-light leading-none text-[#F4F4F0]">{z.name}</span>
                  <span className="relative h-px min-w-4 flex-1 bg-white/10">
                    <span
                      className="bar-grow absolute left-0 top-0 block h-px bg-[#C2A878] shadow-[0_0_8px_rgba(194,168,120,0.6)]"
                      style={{ "--w": `${z.score}%`, animationDelay: `${0.3 + i * 0.06}s` }}
                    />
                  </span>
                  <span className="font-jetbrains text-sm text-[#C2A878]">{z.score}%</span>
                  {z.comment && (
                    <ChevronDown
                      size={13}
                      strokeWidth={1.5}
                      className={`shrink-0 transition-colors duration-300 ${
                        openZone === i ? "rotate-180 text-[#C2A878]" : "text-[#525252]"
                      }`}
                    />
                  )}
                </button>
                {openZone === i && z.comment && (
                  <p
                    className="rise-in overflow-hidden pb-4 pl-9 pr-2 text-xs leading-relaxed text-[#A3A3A3]"
                    data-testid={`ai-zone-comment-${i}`}
                  >
                    {z.comment}
                  </p>
                )}
              </div>
            ))}
          </div>

          {analysis.summary && (
            <p
              className="rise-in px-6 py-6 text-sm leading-relaxed text-[#A3A3A3]"
              style={{ animationDelay: "0.4s" }}
              data-testid="ai-summary"
            >
              {analysis.summary}
            </p>
          )}

          {analysis.current_style && (
            <div
              className="rise-in mx-6 mb-6 border border-[#C2A878]/25 bg-[#C2A878]/[0.04] p-5"
              style={{ animationDelay: "0.46s" }}
              data-testid="ai-current-style"
            >
              <p className="font-jetbrains text-[10px] uppercase tracking-[0.3em] text-[#C2A878]">
                ( Ваша текущая стрижка )
              </p>
              <p className="mt-3 text-sm leading-relaxed text-[#A3A3A3]">
                {analysis.current_style}
              </p>
            </div>
          )}
          </>
          )}

          {analysisTab === "styles" && (
          <>

          {!tryOnImage && (tryOnLoading !== -1 || refLoading) && (
            <div className="border-b border-white/10 px-6 py-10" data-testid="try-on-pending">
              <p className="mb-6 font-jetbrains text-xs uppercase tracking-[0.35em] text-[#C2A878]">
                ( Примерка )
              </p>
              <div className="flex items-center gap-3 text-sm text-[#A3A3A3]">
                <Loader2 size={16} className="animate-spin text-[#C2A878]" />
                ИИ рисует результат — обычно 30–60 секунд
              </div>
              <p className="mt-3 text-xs leading-relaxed text-[#525252]">
                При нагрузке на ИИ может занять пару минут — сервер сам повторит попытку. Генерация идёт на сервере: даже при обрыве связи результат сохранится в истории кабинета.
              </p>
            </div>
          )}

          {tryOnImage && (
            <div
              ref={tryOnRef}
              className="rise-in border-b border-white/10 px-6 py-10"
              data-testid="try-on-result"
            >
              <p className="mb-6 font-jetbrains text-xs uppercase tracking-[0.35em] text-[#C2A878]">
                ( Примерка )
              </p>
              <p className="mb-6 text-sm leading-relaxed text-[#A3A3A3]">{tryOnRec}</p>
              <img
                src={showBefore ? photoUrl : tryOnImage}
                alt={showBefore ? "Фото до примерки" : "Фото после примерки"}
                className="w-full"
                draggable={false}
                data-testid="try-on-image"
              />
              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => setShowBefore(true)}
                  className={`flex-1 border py-2 font-jetbrains text-[10px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                    showBefore
                      ? "border-[#C2A878] bg-[#C2A878]/10 text-[#C2A878]"
                      : "border-white/15 text-[#A3A3A3] hover:border-[#C2A878]/50 hover:text-[#C2A878]"
                  }`}
                  data-testid="try-on-before-button"
                >
                  До
                </button>
                <button
                  onClick={() => setShowBefore(false)}
                  className={`flex-1 border py-2 font-jetbrains text-[10px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                    !showBefore
                      ? "border-[#C2A878] bg-[#C2A878]/10 text-[#C2A878]"
                      : "border-white/15 text-[#A3A3A3] hover:border-[#C2A878]/50 hover:text-[#C2A878]"
                  }`}
                  data-testid="try-on-after-button"
                >
                  После
                </button>
              </div>
              {tryOnRemaining !== null && (
                <p
                  className="mt-4 text-right font-jetbrains text-[10px] uppercase tracking-[0.2em] text-[#525252]"
                  data-testid="try-on-remaining"
                >
                  Осталось примерок · <span className="text-[#C2A878]">{tryOnRemaining}</span>
                </p>
              )}
              <button
                onClick={handleSaveTryOn}
                className="mt-4 flex w-full items-center justify-center gap-2 border border-white/15 py-3 font-jetbrains text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3] transition-colors duration-300 hover:border-[#C2A878]/50 hover:text-[#C2A878]"
                data-testid="try-on-save-button"
              >
                {canSave ? (
                  <Download size={12} strokeWidth={1.5} />
                ) : (
                  <Lock size={12} strokeWidth={1.5} />
                )}
                Сохранить фото
              </button>

              {WARDROBE_ENABLED && !lookImage && !lookOpen && (
                <button
                  onClick={openLookPanel}
                  className="mt-3 flex w-full items-center justify-center gap-2 border border-[#C2A878] py-3 font-jetbrains text-[10px] uppercase tracking-[0.2em] text-[#C2A878] transition-colors duration-300 hover:bg-[#C2A878] hover:text-[#0A0A0A]"
                  data-testid="look-open-button"
                >
                  <Shirt size={12} strokeWidth={1.5} />
                  Подобрать образ к стрижке
                </button>
              )}

              {WARDROBE_ENABLED && lookOpen && !lookImage && (
                <div className="mt-4 border border-white/10 p-4" data-testid="look-panel">
                  {!lookPreview && !lookLoading && !lookGenerating && (
                    <>
                      <p className="font-jetbrains text-[9px] uppercase tracking-[0.25em] text-[#525252]">
                        Откуда берём одежду
                      </p>
                      <div className="mt-3 space-y-2">
                        <button
                          onClick={() => previewLook("wardrobe")}
                          className="flex w-full items-center justify-center gap-2 border border-white/15 py-3 font-jetbrains text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3] transition-colors duration-300 hover:border-[#C2A878]/50 hover:text-[#C2A878]"
                          data-testid="look-mode-wardrobe"
                        >
                          Из моего гардероба
                        </button>
                        <button
                          onClick={() => previewLook("recommended")}
                          className="flex w-full items-center justify-center gap-2 border border-white/15 py-3 font-jetbrains text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3] transition-colors duration-300 hover:border-[#C2A878]/50 hover:text-[#C2A878]"
                          data-testid="look-mode-recommended"
                        >
                          Рекомендуемый образ
                        </button>
                      </div>
                    </>
                  )}
                  {(lookLoading || lookGenerating) && (
                    <div className="flex items-center gap-3 text-xs text-[#A3A3A3]" data-testid="look-loading">
                      <Loader2 size={14} className="animate-spin text-[#C2A878]" />
                      {lookGenerating
                        ? "ИИ рисует полный образ — обычно 30–60 секунд"
                        : "Стилист подбирает образ под стрижку…"}
                    </div>
                  )}
                  {lookPreview && !lookLoading && !lookGenerating && (
                    <div data-testid="look-preview">
                      {lookPreview.mode === "wardrobe" && lookPreview.item_ids.length > 0 && (
                        <div className="flex gap-2 overflow-x-auto pb-1">
                          {lookPreview.item_ids
                            .map((id) => (lookItems || []).find((it) => it._id === id))
                            .filter(Boolean)
                            .map((it) => (
                              <img
                                key={it._id}
                                src={`${API}/images/${it.image_id}`}
                                alt={it.name}
                                className="h-20 w-20 shrink-0 border border-white/10 object-cover"
                                draggable={false}
                              />
                            ))}
                        </div>
                      )}
                      <p className="mt-3 font-cormorant text-xl font-light leading-snug text-[#F4F4F0]" data-testid="look-preview-title">
                        {lookPreview.title}
                      </p>
                      {lookPreview.why && (
                        <p className="mt-2 text-xs leading-relaxed text-[#A3A3A3]">{lookPreview.why}</p>
                      )}
                      <button
                        onClick={() => askSpend(2, applyLook)}
                        className="mt-4 flex w-full items-center justify-center gap-2 bg-[#C2A878] py-3 font-jetbrains text-[10px] uppercase tracking-[0.2em] text-[#0A0A0A] transition-colors duration-300 hover:bg-[#D4BC8A]"
                        data-testid="look-apply-button"
                      >
                        <Sparkles size={12} strokeWidth={1.5} />
                        Примерить · 2 генерации
                      </button>
                      <button
                        onClick={() => previewLook(lookPreview.mode)}
                        className="mt-2 flex w-full items-center justify-center gap-2 py-2 font-jetbrains text-[9px] uppercase tracking-[0.2em] text-[#525252] transition-colors duration-300 hover:text-[#C2A878]"
                        data-testid="look-retry-button"
                      >
                        Другой вариант
                      </button>
                    </div>
                  )}
                </div>
              )}

              {WARDROBE_ENABLED && lookImage && (
                <div className="mt-4 border border-[#C2A878]/25 bg-[#C2A878]/[0.04] p-4" data-testid="look-result">
                  <p className="font-jetbrains text-[9px] uppercase tracking-[0.25em] text-[#C2A878]">
                    ( Полный образ{lookTitle ? ` · ${lookTitle}` : ""} )
                  </p>
                  <img
                    src={lookImage}
                    alt="Полный образ — одежда и стрижка"
                    className="mt-3 w-full"
                    draggable={false}
                    data-testid="look-image"
                  />
                </div>
              )}
            </div>
          )}

          {Array.isArray(analysis.recommendations) && analysis.recommendations.length > 0 && (
            <div className="px-6 pt-4">
              <p className="mb-4 font-jetbrains text-xs uppercase tracking-[0.35em] text-[#C2A878]">
                ( Рекомендации )
              </p>
              <div className="mb-2 flex flex-wrap gap-2" data-testid="length-filters">
                {REC_FILTERS.map(([val, label]) => (
                  <button
                    key={val}
                    onClick={() => setRecFilter(val)}
                    className={`border px-4 py-2 font-jetbrains text-[10px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                      recFilter === val
                        ? "border-[#C2A878] bg-[#C2A878]/10 text-[#C2A878]"
                        : "border-white/15 text-[#A3A3A3] hover:border-[#C2A878]/50 hover:text-[#C2A878]"
                    }`}
                    data-testid={`length-filter-${val}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {analysis.recommendations.filter((r) => recFilter === "all" || r.length === recFilter).length === 0 && (
                <p className="border-t border-white/10 py-8 text-sm text-[#525252]" data-testid="length-filter-empty">
                  Нет стрижек такой длины — попробуйте другой фильтр.
                </p>
              )}
              <div className="grid grid-cols-2 gap-3 pt-4" data-testid="recommendations-grid">
              {analysis.recommendations
                .map((r, i) => ({ r, i }))
                .filter(({ r }) => recFilter === "all" || r.length === recFilter)
                .map(({ r, i }) => (
                <div
                  key={i}
                  className="rise-in flex flex-col border border-white/10 p-4"
                  style={{ animationDelay: `${Math.min(i, 6) * 0.06}s` }}
                  data-testid={`ai-recommendation-${i}`}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    {typeof r.match === "number" ? (
                      <span
                        className="font-cormorant text-3xl font-light leading-none text-[#C2A878]"
                        data-testid={`ai-rec-match-${i}`}
                      >
                        {r.match}%
                      </span>
                    ) : (
                      <span />
                    )}
                    {LENGTH_RU[r.length] && (
                      <span
                        className="border border-[#C2A878]/30 px-2 py-0.5 font-jetbrains text-[8px] uppercase tracking-[0.15em] text-[#C2A878]"
                        data-testid={`ai-rec-length-${i}`}
                      >
                        {LENGTH_RU[r.length]}
                      </span>
                    )}
                  </div>
                  <p className="mt-3 font-cormorant text-xl font-light leading-tight text-[#F4F4F0]">{r.name}</p>
                  {r.why && (
                    <p className="mt-2 text-[11px] leading-relaxed text-[#A3A3A3]">{r.why}</p>
                  )}
                  <div className="mt-auto flex flex-col gap-2 pt-4">
                    <button
                      onClick={() => (triedMap[r.name] ? runTryOn(r, i) : askSpend(1, () => runTryOn(r, i)))}
                      disabled={tryOnLoading !== -1}
                      className={`flex items-center justify-center gap-2 border border-[#C2A878] px-3 py-3 font-jetbrains text-[10px] uppercase tracking-[0.2em] text-[#C2A878] transition-colors duration-500 hover:bg-[#C2A878] hover:text-[#0A0A0A] disabled:cursor-not-allowed disabled:opacity-40 ${
                        triedMap[r.name] ? "bg-[#C2A878]/10" : ""
                      }`}
                      data-testid={`try-on-button-${i}`}
                    >
                      {tryOnLoading === i ? (
                        <>
                          <Loader2 size={12} className="animate-spin" />
                          Примеряем
                        </>
                      ) : triedMap[r.name] ? (
                        <>
                          <Eye size={12} strokeWidth={1.5} />
                          Смотреть результат
                        </>
                      ) : (
                        "Примерить"
                      )}
                    </button>
                    <button
                      onClick={() => runTechCard(r, i)}
                      disabled={techLoading}
                      className="flex items-center justify-center gap-2 border border-white/15 px-3 py-2.5 font-jetbrains text-[9px] uppercase tracking-[0.2em] text-[#A3A3A3] transition-colors duration-500 hover:border-[#C2A878]/60 hover:text-[#C2A878] disabled:cursor-not-allowed disabled:opacity-40"
                      data-testid={`tech-card-button-${i}`}
                    >
                      {techLoading && techCardIdx === i ? (
                        <Loader2 size={12} className="animate-spin" />
                      ) : isMaster ? (
                        <Ruler size={12} strokeWidth={1.5} />
                      ) : (
                        <Lock size={12} strokeWidth={1.5} />
                      )}
                      {isMaster ? "Техкарта" : "Техкарта Pro"}
                    </button>
                  </div>
                </div>
              ))}
              </div>
              {techCard && (
                <div className="mt-6 border border-[#C2A878]/20 p-5" data-testid="tech-card-result">
                      <p className="font-cormorant text-2xl font-light text-[#F4F4F0]">{techCard.title}</p>
                      {Array.isArray(techCard.zones) && techCard.zones.length > 0 && (
                        <div className="mt-6">
                          <p className="mb-3 font-jetbrains text-[10px] uppercase tracking-[0.3em] text-[#C2A878]">
                            ( Зоны )
                          </p>
                          {techCard.zones.map((z, zi) => (
                            <div key={zi} className="border-t border-white/10 py-3" data-testid={`tech-zone-${zi}`}>
                              <p className="text-sm text-[#F4F4F0]">{z.name}</p>
                              <p className="mt-1 text-xs leading-relaxed text-[#A3A3A3]">
                                {z.length} · {z.angle} · {z.technique}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                      {Array.isArray(techCard.steps) && techCard.steps.length > 0 && (
                        <div className="mt-6">
                          <p className="mb-3 font-jetbrains text-[10px] uppercase tracking-[0.3em] text-[#C2A878]">
                            ( Порядок работы )
                          </p>
                          {techCard.steps.map((s, si) => (
                            <p key={si} className="mt-2 text-xs leading-relaxed text-[#A3A3A3]" data-testid={`tech-step-${si}`}>
                              <span className="text-[#C2A878]">{String(si + 1).padStart(2, "0")}</span> · {s}
                            </p>
                          ))}
                        </div>
                      )}
                      {Array.isArray(techCard.tools) && techCard.tools.length > 0 && (
                        <p className="mt-6 text-xs leading-relaxed text-[#A3A3A3]" data-testid="tech-tools">
                          <span className="text-[#C2A878]">Инструменты: </span>
                          {techCard.tools.join(" · ")}
                        </p>
                      )}
                      {Array.isArray(techCard.control) && techCard.control.length > 0 && (
                        <p className="mt-3 text-xs leading-relaxed text-[#A3A3A3]" data-testid="tech-control">
                          <span className="text-[#C2A878]">Контроль: </span>
                          {techCard.control.join(" · ")}
                        </p>
                      )}
                      {techCard.tip && (
                        <p className="mt-6 border-l border-[#C2A878]/40 pl-4 text-xs italic leading-relaxed text-[#A3A3A3]" data-testid="tech-tip">
                          {techCard.tip}
                        </p>
                      )}
                </div>
              )}

              <div className="border-t border-white/10 px-6 py-8" data-testid="ref-tryon-block">
                <p className="mb-2 font-jetbrains text-xs uppercase tracking-[0.35em] text-[#C2A878]">
                  ( Своя стрижка )
                </p>
                <p className="mb-6 text-sm leading-relaxed text-[#A3A3A3]">
                  Загрузите фото причёски, которая нравится, — примерим её на вас.
                </p>
                <input
                  ref={refInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleRefFile}
                  className="hidden"
                  data-testid="ref-upload-input"
                />
                <div className="flex flex-wrap items-center gap-3">
                  {refImage && (
                    <img
                      src={refImage}
                      alt="Референс стрижки"
                      className="h-20 w-20 border border-white/15 object-cover"
                      data-testid="ref-preview"
                    />
                  )}
                  <button
                    onClick={() => refInputRef.current?.click()}
                    className="flex items-center gap-2 border border-white/15 px-6 py-3.5 font-jetbrains text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3] transition-colors duration-300 hover:border-[#C2A878]/60 hover:text-[#C2A878]"
                    data-testid="ref-upload-button"
                  >
                    <ImagePlus size={13} strokeWidth={1.5} />
                    {refImage ? "Другое фото" : "Фото референса"}
                  </button>
                  <button
                    onClick={() => askSpend(1, runRefTryOn)}
                    disabled={!refImage || refLoading || tryOnLoading !== -1}
                    className="flex items-center gap-2 border border-[#C2A878] px-8 py-3.5 font-jetbrains text-[10px] uppercase tracking-[0.25em] text-[#C2A878] transition-colors duration-300 hover:bg-[#C2A878] hover:text-[#0A0A0A] disabled:cursor-not-allowed disabled:opacity-40"
                    data-testid="ref-tryon-button"
                  >
                    {refLoading ? (
                      <>
                        <Loader2 size={13} className="animate-spin" />
                        Примеряем
                      </>
                    ) : (
                      "Примерить"
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
          </>
          )}

          {analysisTab === "care" && (
            <div className="px-6 pb-10 pt-6" data-testid="care-panel">
              {!haircare && !careLoading && (
                <div className="rise-in">
                  <p className="font-jetbrains text-xs uppercase tracking-[0.35em] text-[#C2A878]">
                    ( Подбор ухода )
                  </p>
                  <p className="mt-4 text-sm leading-relaxed text-[#A3A3A3]">
                    ИИ оценит тип и состояние ваших волос по этому фото и соберёт уход:
                    шампунь, маску, несмываемое средство и укладку — с примерами и ценами.
                  </p>
                  <button
                    onClick={() => askSpend(1, runCare)}
                    className="mt-6 flex w-full items-center justify-center gap-3 bg-[#C2A878] py-4 font-jetbrains text-xs uppercase tracking-[0.25em] text-[#0A0A0A] transition-colors duration-500 hover:bg-[#D4BC8A]"
                    data-testid="care-run-button"
                  >
                    Подобрать уход · 1 примерка
                  </button>
                </div>
              )}
              {careLoading && <AnalysisScan photoUrl={photoUrl} note="ИИ изучает ваши волосы" />}
              {haircare && !careLoading && (
                <div className="rise-in" data-testid="care-result">
                  {haircare.image_id && (
                    <div className="mb-8">
                      <img
                        src={`${API}/images/${haircare.image_id}`}
                        alt="Карточка подбора ухода"
                        className="w-full border border-[#C2A878]/30"
                        data-testid="care-card-image"
                      />
                      <button
                        onClick={saveCareCard}
                        className="mt-4 flex w-full items-center justify-center gap-2 border border-white/15 py-3 font-jetbrains text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3] transition-colors duration-300 hover:border-[#C2A878]/50 hover:text-[#C2A878]"
                        data-testid="care-save-button"
                      >
                        <Download size={12} strokeWidth={1.5} />
                        Сохранить карточку
                      </button>
                    </div>
                  )}
                  <div className="border-y border-white/10 py-4">
                    <p className="font-jetbrains text-[9px] uppercase tracking-[0.3em] text-[#C2A878]">
                      ( Ваши волосы )
                    </p>
                    <h2
                      className="mt-2 font-cormorant text-3xl font-light leading-tight tracking-tight text-[#F4F4F0]"
                      data-testid="care-hair-type"
                    >
                      {haircare.hair_type}
                    </h2>
                  </div>
                  {haircare.condition && (
                    <p
                      className="mt-4 inline-block border border-[#C2A878]/40 bg-[#C2A878]/10 px-4 py-2 font-jetbrains text-xs uppercase tracking-[0.15em] text-[#C2A878]"
                      data-testid="care-condition"
                    >
                      {haircare.condition}
                    </p>
                  )}
                  {haircare.summary && (
                    <p className="mt-5 text-sm leading-relaxed text-[#A3A3A3]" data-testid="care-summary">
                      {haircare.summary}
                    </p>
                  )}

                  {Array.isArray(haircare.products) && haircare.products.length > 0 && (
                    <div className="mt-8">
                      <p className="mb-4 font-jetbrains text-xs uppercase tracking-[0.35em] text-[#C2A878]">
                        ( Ваш набор )
                      </p>
                      <div className="space-y-3">
                        {haircare.products.map((p, i) => (
                          <div
                            key={i}
                            className="rise-in border border-white/10 p-5"
                            style={{ animationDelay: `${Math.min(i, 6) * 0.06}s` }}
                            data-testid={`care-product-${i}`}
                          >
                            <p className="font-jetbrains text-[11px] uppercase tracking-[0.25em] text-[#C2A878]">
                              {p.category}
                            </p>
                            {p.advice && (
                              <p className="mt-2.5 text-sm leading-relaxed text-[#F4F4F0]/90">{p.advice}</p>
                            )}
                            {p.examples && (
                              <p className="mt-2 text-xs leading-relaxed text-[#A3A3A3]">
                                <span className="text-[#C2A878]">Примеры: </span>
                                {p.examples}
                              </p>
                            )}
                            {p.why && (
                              <p className="mt-2 border-l border-[#C2A878]/30 pl-3 text-xs italic leading-relaxed text-[#525252]">
                                {p.why}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {Array.isArray(haircare.routine) && haircare.routine.length > 0 && (
                    <div className="mt-8" data-testid="care-routine">
                      <p className="mb-3 font-jetbrains text-[10px] uppercase tracking-[0.3em] text-[#C2A878]">
                        ( Ритуал недели )
                      </p>
                      {haircare.routine.map((s, i) => (
                        <p key={i} className="mt-2 text-sm leading-relaxed text-[#A3A3A3]">
                          <span className="text-[#C2A878]">{String(i + 1).padStart(2, "0")}</span> · {s}
                        </p>
                      ))}
                    </div>
                  )}

                  {Array.isArray(haircare.avoid) && haircare.avoid.length > 0 && (
                    <div className="mt-8" data-testid="care-avoid">
                      <p className="mb-3 font-jetbrains text-[10px] uppercase tracking-[0.3em] text-[#525252]">
                        ( Лучше не надо )
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {haircare.avoid.map((a, i) => (
                          <span key={i} className="border border-white/15 px-4 py-2 font-jetbrains text-xs text-[#A3A3A3]">
                            {a}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {tryOnRemaining !== null && (
                    <p className="mt-6 font-jetbrains text-[10px] uppercase tracking-[0.2em] text-[#525252]" data-testid="care-remaining">
                      Осталось примерок · <span className="text-[#C2A878]">{tryOnRemaining}</span>
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
        </div>
      )}

      {/* Подтверждение списания примерок перед любой генерацией */}
      {spendAsk && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-[#0A0A0A]/85 px-6 backdrop-blur-sm"
          onClick={() => setSpendAsk(null)}
          data-testid="spend-confirm-modal"
        >
          <div
            className="w-full max-w-xs border border-white/10 bg-[#0A0A0A] p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="font-jetbrains text-[10px] uppercase tracking-[0.3em] text-[#C2A878]">
              ( Подтверждение )
            </p>
            <p className="mt-4 font-cormorant text-2xl font-light leading-snug text-[#F4F4F0]" data-testid="spend-confirm-text">
              Списать {spendAsk.cost} {pluralTryons(spendAsk.cost)}?
            </p>
            {tryOnRemaining !== null && (
              <p className="mt-2 text-xs leading-relaxed text-[#A3A3A3]">
                Сейчас доступно: {tryOnRemaining}
              </p>
            )}
            <button
              onClick={() => {
                const run = spendAsk.run;
                setSpendAsk(null);
                run();
              }}
              className="mt-5 flex w-full items-center justify-center gap-2 bg-[#C2A878] py-3 font-jetbrains text-[10px] uppercase tracking-[0.2em] text-[#0A0A0A] transition-colors duration-300 hover:bg-[#D4BC8A]"
              data-testid="spend-confirm-yes"
            >
              Да, продолжить
            </button>
            <button
              onClick={() => setSpendAsk(null)}
              className="mt-2 flex w-full items-center justify-center py-2 font-jetbrains text-[9px] uppercase tracking-[0.2em] text-[#525252] transition-colors duration-300 hover:text-[#C2A878]"
              data-testid="spend-confirm-no"
            >
              Отмена
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
